import type {
  Run,
  Execution,
  NodeExecution,
  RetryAttempt,
  RunStatus,
  NodeExecutionStatus,
  TriggerHistory,
  TriggerHistoryStatus,
} from '@/types/monitoring';
import { parseWorkflowId } from '@/types/logicApps';
import {
  isLocalMode,
} from './localRuntimeClient';
import { getAppUrl, appFetch, AppFetchError } from './appClient';
import { apiVersions, getLogicEndpoint } from './endpoints';
import { MockDefinition, MockNodeInfo, TriggerMockPayload } from '@/types/templateMode';
import type { RequestTriggerContent, TriggerContentState } from '@/types/monitoring';
import { telemetryService } from '@/services/telemetryService';
import { extractErrorMessage } from './apiError';

export type MonitoringFetchOptions = {
  projectName: string;
  appName: string;
  workflowName: string;
  accessToken: string;
};

export type ResubmissionFetchOptions = MonitoringFetchOptions & {
  /** Originating app URL captured before queuing or awaiting authentication. */
  appUrl?: string;
};

// Azure Logic Apps API response types
type AzureRunStatus = 'Running' | 'Succeeded' | 'Failed' | 'Cancelled' | 'Canceled' | 'Waiting' | 'Skipped' | 'Aborted';

/** Workflow definition action structure (from definition, not runtime) */
type WorkflowDefinitionAction = {
  type?: string;
  kind?: string;
  runAfter?: Record<string, string[]>;
  tools?: Record<string, unknown>;
  actions?: Record<string, WorkflowDefinitionAction>;
};

type AzureRunResponse = {
  id: string;
  name: string;
  type: string;
  properties: {
    waitEndTime?: string;
    startTime: string;
    endTime?: string;
    status: AzureRunStatus;
    previousRunId?: string;
    correlation?: {
      clientTrackingId?: string;
    };
    workflow?: {
      id: string;
      name: string;
      type: string;
      kind?: string;
      mode?: string;
      properties?: {
        definition?: {
          actions?: Record<string, WorkflowDefinitionAction>;
          triggers?: Record<string, { type?: string; kind?: string }>;
        };
      };
    };
    trigger?: {
      name: string;
      inputsLink?: ContentLink;
      outputsLink?: ContentLink;
      startTime?: string;
      endTime?: string;
      status?: AzureRunStatus;
    };
    actions?: Record<string, AzureActionRun>;
  };
};

type AzureActionRun = {
  name?: string;
  /** Action type (e.g., "ApiConnection", "Agent", "Foreach", etc.) */
  type?: string;
  inputsLink?: ContentLink;
  outputsLink?: ContentLink;
  startTime?: string;
  endTime?: string;
  status: AzureRunStatus;
  canResubmit?: boolean;
  code?: string;
  error?: {
    code: string;
    message: string;
  };
  /** Repetition count for loops (Foreach/Until) */
  repetitionCount?: number;
  /** Iteration count for Agent actions */
  iterationCount?: number;
  /** Retry attempts the engine made before the action reached its final status. */
  retryHistory?: AzureRetryHistory[];
  /** Agent metadata for FoundryAgentService actions */
  agentMetadata?: {
    modelType?: string;
    agentId?: string;
    threadId?: string;
    conversationId?: string;
    traceId?: string;
  };
};

/**
 * Runtime retry-history entry (FlowOperationRetryHistory). Each entry is a
 * retried attempt, typically a failed attempt, and `error` may be omitted even
 * for failures.
 */
type AzureRetryHistory = {
  startTime?: string;
  endTime?: string;
  code?: string;
  clientRequestId?: string;
  serviceRequestId?: string;
  error?: {
    code: string;
    message: string;
  };
};

type ContentLink = {
  uri?: string;
  contentVersion?: string;
  contentSize?: number;
  contentHash?: {
    algorithm?: string;
    value?: string;
  };
  /**
   * Runtime metadata. ForEach scope actions emit `foreachItemsCount` here
   * (the canonical iteration count source — `repetitionCount` is only set on
   * Agent message-iteration actions).
   */
  metadata?: {
    foreachItemsCount?: number;
  };
};

type AzureRunsResponse = {
  value: AzureRunResponse[];
  nextLink?: string;
};

// Transform Azure status to our RunStatus type
const transformRunStatus = (azureStatus: AzureRunStatus): RunStatus => {
  switch (azureStatus) {
    case 'Running':
    case 'Waiting':
      return 'running';
    case 'Succeeded':
      return 'succeeded';
    case 'Failed':
    case 'Aborted':
      return 'failed';
    case 'Cancelled':
    case 'Canceled':
      return 'cancelled';
    case 'Skipped':
      return 'cancelled';
    default:
      return 'failed';
  }
};

// Transform Azure status to NodeExecutionStatus
const transformNodeStatus = (azureStatus: AzureRunStatus): NodeExecutionStatus => {
  switch (azureStatus) {
    case 'Running':
    case 'Waiting':
      return 'running';
    case 'Succeeded':
      return 'succeeded';
    case 'Failed':
    case 'Aborted':
      return 'failed';
    case 'Cancelled':
    case 'Canceled':
      // Cancelled is its own status — the action was reached then stopped
      // (e.g. Terminate ran in another branch). Don't collapse to 'failed':
      // there is no error to surface and the red "Action failed" banner is
      // misleading. See issue #1708.
      return 'cancelled';
    case 'Skipped':
      return 'skipped';
    default:
      return 'failed';
  }
};

// Calculate duration in milliseconds
const calculateDuration = (startTime?: string, endTime?: string): number | undefined => {
  if (!startTime || !endTime) return undefined;
  const start = new Date(startTime).getTime();
  const end = new Date(endTime).getTime();
  const duration = end - start;
  return duration >= 0 ? duration : undefined;
};

// Transform the runtime retry-history array into RetryAttempt[]. Returns
// undefined when there are no attempts so callers can omit the field entirely.
const transformRetryHistory = (
  history?: AzureRetryHistory[]
): RetryAttempt[] | undefined => {
  if (!history || history.length === 0) return undefined;
  const attempts = history
    .filter((entry): entry is AzureRetryHistory & { startTime: string } => !!entry.startTime)
    .map((entry) => {
      const attempt: RetryAttempt = { startTime: entry.startTime };
      if (entry.endTime) attempt.endTime = entry.endTime;
      const durationMs = calculateDuration(entry.startTime, entry.endTime);
      if (durationMs !== undefined) attempt.durationMs = durationMs;
      if (entry.code) attempt.code = entry.code;
      if (entry.clientRequestId) attempt.clientRequestId = entry.clientRequestId;
      if (entry.serviceRequestId) attempt.serviceRequestId = entry.serviceRequestId;
      if (entry.error) attempt.error = { code: entry.error.code, message: entry.error.message };
      return attempt;
    });
  // Keep the "omit when none" contract even if every entry was filtered out
  // (e.g. all lacked a startTime).
  return attempts.length > 0 ? attempts : undefined;
};

// Check if workflow kind indicates an agentic workflow
// The kind field value is 'Agent' for agent workflows
const isAgentWorkflowKind = (kind?: string): boolean => {
  if (!kind) return false;
  return kind.toLowerCase() === 'agent';
};

// Transform Azure run response to our Run type
const transformRun = (azureRun: AzureRunResponse, fallbackWorkflowName: string): Run => {
  const { properties } = azureRun;
  const duration = calculateDuration(properties.startTime, properties.endTime);

  // Extract workflow name from the workflow ID if available, otherwise use fallback
  const workflowName = properties.workflow?.id
    ? parseWorkflowId(properties.workflow.id) ?? fallbackWorkflowName
    : fallbackWorkflowName;

  // Check if this was a draft run (mode === "Draft")
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isDraftRun = (properties.workflow as any)?.mode === 'Draft';
  
  // Extract workflow definition snapshot from the run response
  // This is the definition that was active when this run executed
  const workflowSnapshot = properties.workflow?.properties?.definition 
    ? {
        definition: properties.workflow.properties.definition,
        kind: properties.workflow?.kind,
      }
    : undefined;

  // Build node executions from trigger and actions
  const nodeExecutions: NodeExecution[] = [];

  // Add trigger as first node execution
  if (properties.trigger) {
    nodeExecutions.push({
      nodeId: `trigger-${properties.trigger.name}`,
      nodeName: properties.trigger.name,
      nodeType: 'Trigger',
      status: properties.trigger.status ? transformNodeStatus(properties.trigger.status) : 'succeeded',
      startTime: properties.trigger.startTime,
      endTime: properties.trigger.endTime,
      duration: calculateDuration(properties.trigger.startTime, properties.trigger.endTime),
      hasInput: !!properties.trigger.inputsLink,
      hasOutput: !!properties.trigger.outputsLink,
    });
  }

  // Add actions from run properties (runtime execution data)
  if (properties.actions && Object.keys(properties.actions).length > 0) {
    // Build parent scope mapping from workflow definition
    // ForEach/Until scope actions contain child actions in their 'actions' property
    const parentScopeMap = new Map<string, string>();
    const workflowDef = properties.workflow?.properties?.definition;
    if (workflowDef?.actions) {
      const buildParentMap = (
        actions: Record<string, WorkflowDefinitionAction>,
        nearestLoopScope?: string
      ) => {
        for (const [name, action] of Object.entries(actions)) {
          if (nearestLoopScope) {
            parentScopeMap.set(name, nearestLoopScope);
          }
          if (action.actions) {
            const isLoopScope = action.type === 'Foreach' || action.type === 'Until';
            buildParentMap(
              action.actions,
              isLoopScope ? name : nearestLoopScope
            );
          }
        }
      };
      buildParentMap(workflowDef.actions);
    }

    for (const [actionName, action] of Object.entries(properties.actions)) {
      // Determine node type: prefer inline type from run data, fall back to
      // workflow definition type (inline $expand often omits the type field).
      // Agent actions are always top-level, so top-level lookup is sufficient.
      const actionType = action.type || workflowDef?.actions?.[actionName]?.type || 'Action';
      const nodeExecution: NodeExecution = {
        nodeId: `action-${actionName}`,
        nodeName: action.name ?? actionName,
        nodeType: actionType,
        status: transformNodeStatus(action.status),
        startTime: action.startTime,
        endTime: action.endTime,
        duration: calculateDuration(action.startTime, action.endTime),
        hasInput: !!action.inputsLink,
        hasOutput: !!action.outputsLink,
      };
      if (typeof action.canResubmit === 'boolean') {
        nodeExecution.canResubmit = action.canResubmit;
      }
      // Include error details if the action failed
      if (action.error) {
        nodeExecution.error = {
          code: action.error.code,
          message: action.error.message,
        };
      }
      // Include retry attempts when the engine retried this action.
      const retryHistory = transformRetryHistory(action.retryHistory);
      if (retryHistory) {
        nodeExecution.retryHistory = retryHistory;
      }
      // Include iteration count for Agent actions and loop scopes.
      // Agent actions emit `iterationCount`; chatMessage iteration agents emit
      // `repetitionCount`; ForEach scopes emit `inputsLink.metadata.foreachItemsCount`.
      // We try them in that order.
      if (action.iterationCount !== undefined) {
        nodeExecution.iterationCount = action.iterationCount;
      } else if (action.repetitionCount !== undefined) {
        nodeExecution.iterationCount = action.repetitionCount;
      } else if (action.inputsLink?.metadata?.foreachItemsCount !== undefined) {
        nodeExecution.iterationCount = action.inputsLink.metadata.foreachItemsCount;
      }
      // Include agent metadata for FoundryAgentService actions
      if (action.agentMetadata) {
        nodeExecution.agentMetadata = {
          modelType: action.agentMetadata.modelType,
          agentId: action.agentMetadata.agentId,
          threadId: action.agentMetadata.threadId,
          conversationId: action.agentMetadata.conversationId,
          traceId: action.agentMetadata.traceId,
        };
      }
      // Set parent scope for ForEach/Until inner actions
      const parentScope = parentScopeMap.get(actionName);
      if (parentScope) {
        nodeExecution.parentNodeId = parentScope;
      }
      nodeExecutions.push(nodeExecution);
    }

    // Propagate iterationCount from parent scope to child actions.
    // The runtime emits the iteration count on the scope (via inputsLink
    // metadata.foreachItemsCount for ForEach, or repetitionCount for chatMessage
    // agents) — it doesn't set it on child actions. Children need the count to
    // trigger repetition fetching.
    const execByName = new Map(nodeExecutions.map((ne) => [ne.nodeName, ne]));
    for (const ne of nodeExecutions) {
      if (ne.parentNodeId && ne.iterationCount === undefined) {
        const parent = execByName.get(ne.parentNodeId);
        if (parent?.iterationCount !== undefined && parent.iterationCount > 0) {
          ne.iterationCount = parent.iterationCount;
        }
      }
    }
  } else if (properties.workflow?.properties?.definition?.actions) {
    // Fallback: For running workflows where actions haven't executed yet,
    // use the workflow definition to show actions with 'running' or 'pending' status
    //
    // IMPORTANT: For chatMessage workflows (trigger = 'when_a_chat_message_is_received'),
    // the actions in the workflow definition are INSIDE the internal.MessageIterationOrchestrator
    // and should NOT be shown as top-level actions. They will be fetched via the
    // chatMessage iteration API (fetchChatMessageIterationActions).
    const isChatMessageWorkflow = properties.trigger?.name === 'when_a_chat_message_is_received';
    if (isChatMessageWorkflow) {
      // For chatMessage workflows, don't synthesize actions from definition
      // The actual executed actions are inside the orchestrator and fetched separately
      console.log('[transformRun] ChatMessage workflow detected, skipping definition-based actions');
      
      // Add a synthetic execution entry for the internal.MessageIterationOrchestrator
      // This is used by the LogsPanel to detect chatMessage workflows and render actions
      // from the iteration API instead of the definition.
      nodeExecutions.push({
        nodeId: 'action-internal.MessageIterationOrchestrator',
        nodeName: 'internal.MessageIterationOrchestrator',
        nodeType: 'Agent',
        status: properties.status === 'Running' ? 'running' : 'succeeded',
      });
    } else if (transformRunStatus(properties.status) === 'running') {
      // Terminal runs must leave unreported actions absent so the canvas can apply its skipped fallback.
      const definitionActions = properties.workflow.properties.definition.actions;
      const isRunning = properties.status === 'Running';

      for (const [actionName, action] of Object.entries(definitionActions)) {
        // Determine if this action should be running or pending
        // If trigger succeeded and action runs after trigger, it might be running
        const triggerName = properties.trigger?.name;
        const runsAfterTrigger = action.runAfter && triggerName &&
          Object.keys(action.runAfter).some(key => key === triggerName);
        const triggerSucceeded = properties.trigger?.status === 'Succeeded';

        // Determine status: running if trigger succeeded and runs after it, pending otherwise
        let status: 'running' | 'pending' = 'pending';
        if (isRunning && runsAfterTrigger && triggerSucceeded) {
          status = 'running';
        }

        // Determine node type - check for Agent action by presence of 'tools' property
        // Workflow definitions don't always have explicit type, so we infer from structure
        let nodeType = action.type ?? 'Action';
        if (!action.type && action.tools) {
          nodeType = 'Agent';
        }

        const nodeExecution: NodeExecution = {
          nodeId: `action-${actionName}`,
          nodeName: actionName,
          nodeType,
          status,
          // No timing data for actions that haven't executed yet
        };
        nodeExecutions.push(nodeExecution);
      }
    }
  }

  // Sort node executions by start time
  nodeExecutions.sort((a, b) => {
    if (!a.startTime) return 1;
    if (!b.startTime) return -1;
    return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
  });

  // Create a single execution containing all node executions
  const execution: Execution = {
    id: `exec-${azureRun.name}`,
    runId: azureRun.name,
    status: transformRunStatus(properties.status) as 'running' | 'succeeded' | 'failed' | 'cancelled',
    timestamp: properties.startTime,
    duration,
    nodeExecutions,
  };

  // Detect if workflow is agentic based on the workflow kind from run data
  // The kind is at the workflow level (workflow.kind), not nested in properties
  const workflowKind = properties.workflow?.kind;
  const isAgentic = isAgentWorkflowKind(workflowKind);

  return {
    id: azureRun.name,
    workflowId: properties.workflow?.id ?? '',
    workflowName,
    ...(typeof properties.trigger?.name === 'string' && { triggerName: properties.trigger.name }),
    status: transformRunStatus(properties.status),
    startTime: properties.startTime,
    endTime: properties.endTime,
    duration,
    isAgentic,
    executionCount: 1,
    executions: [execution],
    isDraftRun,
    ...(typeof properties.previousRunId === 'string' && { previousRunId: properties.previousRunId }),
    workflowSnapshot,
  };
};

export type RunsPage = {
  runs: Run[];
  nextLink?: string;
};

/**
 * Builds the runs list URL against the Otto control plane.
 *
 * When paginating, the runtime returns a `nextLink` pointing at the ARM
 * `/invoke` URL it was reached through (audience = management.azure.com).
 * Since we authenticate against the Otto control plane, we reuse the base
 * runs URL and just carry the `$skiptoken` across.
 */
const buildRunsListUrl = (opts: MonitoringFetchOptions, continuationToken?: string): string => {
  const params = new URLSearchParams({ 'api-version': apiVersions.otto });
  if (continuationToken) {
    try {
      const skiptoken = new URL(continuationToken).searchParams.get('$skiptoken');
      if (skiptoken) params.set('$skiptoken', skiptoken);
    } catch {
      // malformed continuation — refetching the first page is safer than
      // looping against an un-authable ARM URL
    }
  }
  return `${getAppUrl(opts)}/workflows/${opts.workflowName}/runs?${params.toString()}`;
};

/**
 * Fetches all runs for a workflow (single page, no pagination)
 */
export const fetchRuns = async (opts: MonitoringFetchOptions): Promise<Run[]> => {
  const response = await appFetch<AzureRunsResponse>(buildRunsListUrl(opts), opts.accessToken);
  return response.value.map((azureRun) => transformRun(azureRun, opts.workflowName));
};

/**
 * Fetches a page of runs for a workflow. Pass the previous page's `nextLink`
 * as `continuationToken` to fetch subsequent pages.
 */
export const fetchRunsPage = async (
  opts: MonitoringFetchOptions,
  continuationToken?: string
): Promise<RunsPage> => {
  const response = await appFetch<AzureRunsResponse>(
    buildRunsListUrl(opts, continuationToken),
    opts.accessToken
  );

  const result: RunsPage = {
    runs: response.value.map((azureRun) => transformRun(azureRun, opts.workflowName)),
  };
  if (response.nextLink) {
    result.nextLink = response.nextLink;
  }
  return result;
};

/**
 * Fetches a specific run with expanded actions
 */
export const fetchRun = async (
  opts: MonitoringFetchOptions,
  runId: string
): Promise<Run | null> => {
  const baseUrl = getAppUrl(opts);
  const url = `${baseUrl}/workflows/${opts.workflowName}/runs/${runId}?api-version=${apiVersions.otto}&$expand=properties/actions,workflow/properties`;

  try {
    const azureRun = await appFetch<AzureRunResponse>(url, opts.accessToken);
    return transformRun(azureRun, opts.workflowName);
  } catch (error) {
    console.error('Failed to fetch run:', error);
    return null;
  }
};

/**
 * Run data with content links for preview fetching.
 * Includes trigger and all action input/output links from expanded run data.
 *
 * Both `inputsLink` and `outputsLink` are captured because the expanded run
 * response already carries them in the same payload — surfacing them here lets
 * downstream hooks (variable extraction, retry diagnostics, etc.) skip a
 * follow-up `fetchActionDetails` round-trip per action.
 */
export type RunWithOutputLinks = {
  runId: string;
  status: RunStatus;
  startTime: string;
  endTime: string | undefined;
  /** Trigger output link */
  triggerOutputLink: ContentLink | undefined;
  /** Trigger input link */
  triggerInputLink: ContentLink | undefined;
  /** Action output links keyed by action name (includes agent tool outputs keyed by tool action name like "GetOrderDetails") */
  actionOutputLinks: Record<string, ContentLink | undefined>;
  /** Action input links keyed by action name (includes agent tool inputs keyed by tool action name like "GetOrderDetails") */
  actionInputLinks: Record<string, ContentLink | undefined>;
};

/**
 * Constructs an action output URL from the trigger output URL.
 * The trigger URL pattern is: .../runs/{runId}/contents/TriggerOutputs?...
 * The action URL pattern is: .../runs/{runId}/actions/{actionName}/contents/ActionOutputs?...
 */
export function constructActionOutputUrl(triggerOutputUrl: string, actionName: string): string {
  // Replace /contents/TriggerOutputs with /actions/{actionName}/contents/ActionOutputs
  // Also update the sp parameter to match the new path
  const url = new URL(triggerOutputUrl);

  // Update the path
  const pathParts = url.pathname.split('/');
  const contentsIndex = pathParts.findIndex(p => p === 'contents');
  if (contentsIndex !== -1) {
    // Insert /actions/{actionName} before /contents
    pathParts.splice(contentsIndex, 0, 'actions', encodeURIComponent(actionName));
    // Change TriggerOutputs to ActionOutputs
    const outputsIndex = pathParts.findIndex(p => p === 'TriggerOutputs');
    if (outputsIndex !== -1) {
      pathParts[outputsIndex] = 'ActionOutputs';
    }
    url.pathname = pathParts.join('/');
  }

  // Update the sp (signed permissions) parameter to match the new path
  const sp = url.searchParams.get('sp');
  if (sp) {
    const newSp = sp
      .replace('/contents/TriggerOutputs/', `/actions/${encodeURIComponent(actionName)}/contents/ActionOutputs/`)
      .replace('/contents/TriggerOutputs', `/actions/${encodeURIComponent(actionName)}/contents/ActionOutputs`);
    url.searchParams.set('sp', newSp);
  }

  return url.toString();
}

/**
 * Fetches runs list to find run IDs.
 * Returns basic run info without expanded data.
 */
export const fetchRunsList = async (
  opts: MonitoringFetchOptions
): Promise<Array<{ runId: string; status: RunStatus; startTime: string; endTime: string | undefined }>> => {
  const baseUrl = getAppUrl(opts);
  const url = `${baseUrl}/workflows/${opts.workflowName}/runs?api-version=${apiVersions.otto}`;

  try {
    const response = await appFetch<AzureRunsResponse>(url, opts.accessToken);

    return response.value.map((azureRun) => ({
      runId: azureRun.name,
      status: transformRunStatus(azureRun.properties.status),
      startTime: azureRun.properties.startTime,
      endTime: azureRun.properties.endTime,
    }));
  } catch (error) {
    console.error('Failed to fetch runs list:', error);
    return [];
  }
};

/**
 * Fetches a specific run with expanded action data to get all output links.
 * Uses $expand=properties/actions,workflow/properties to include all action outputsLinks.
 * This works for both built-in operations (Compose, etc.) and API connections.
 */
export const fetchRunWithOutputLinks = async (
  opts: MonitoringFetchOptions,
  runId: string
): Promise<RunWithOutputLinks | null> => {
  const baseUrl = getAppUrl(opts);
  const url = `${baseUrl}/workflows/${opts.workflowName}/runs/${runId}?api-version=${apiVersions.otto}&$expand=properties/actions,workflow/properties`;

  try {
    const azureRun = await appFetch<AzureRunResponse>(url, opts.accessToken);

    // Extract action input + output links. The expanded run response carries
    // both, so capturing them here lets `useNodePreviewData` skip a per-action
    // `fetchActionDetails` round-trip when it only needs the input or output
    // content.
    const actionOutputLinks: Record<string, ContentLink | undefined> = {};
    const actionInputLinks: Record<string, ContentLink | undefined> = {};
    if (azureRun.properties.actions) {
      for (const [actionName, actionData] of Object.entries(azureRun.properties.actions)) {
        actionOutputLinks[actionName] = actionData.outputsLink;
        actionInputLinks[actionName] = actionData.inputsLink;
      }
    }

    return {
      runId: azureRun.name,
      status: transformRunStatus(azureRun.properties.status),
      startTime: azureRun.properties.startTime,
      endTime: azureRun.properties.endTime,
      triggerOutputLink: azureRun.properties.trigger?.outputsLink,
      triggerInputLink: azureRun.properties.trigger?.inputsLink,
      actionOutputLinks,
      actionInputLinks,
    };
  } catch (error) {
    console.error('Failed to fetch run with output links:', error);
    return null;
  }
};

/**
 * Fetches a specific run with expanded action data to get all output links,
 * including agent tool output links for Agent-type actions.
 *
 * For Agent-type actions:
 * 1. Fetches agent repetitions (iterations)
 * 2. For each repetition, fetches the tool actions to get output links
 * 3. Flattens tool links into actionOutputLinks/actionInputLinks keyed by the tool action name (e.g. "GetOrderDetails").
 *
 * Uses $expand=properties/actions,workflow/properties to include all action outputsLinks.
 * This works for both built-in operations (Compose, etc.) and API connections.
 */
export const fetchRunWithAgentToolsWithOutputLinks = async (
  opts: MonitoringFetchOptions,
  runId: string
): Promise<RunWithOutputLinks | null> => {
  const baseUrl = getAppUrl(opts);
  const url = `${baseUrl}/workflows/${opts.workflowName}/runs/${runId}?api-version=${apiVersions.otto}&$expand=properties/actions,workflow/properties`;

  try {
    const azureRun = await appFetch<AzureRunResponse>(url, opts.accessToken);

    // Extract action input + output links. The expanded run response carries
    // both, so capturing them here lets `useNodePreviewData` skip a per-action
    // `fetchActionDetails` round-trip when it only needs the input or output
    // content.
    const actionOutputLinks: Record<string, ContentLink | undefined> = {};
    const actionInputLinks: Record<string, ContentLink | undefined> = {};
    const agentActionNames: string[] = [];

    if (azureRun.properties.actions) {
      // Also check the workflow definition for action types
      const workflowDef = azureRun.properties.workflow?.properties?.definition;

      for (const [actionName, actionData] of Object.entries(azureRun.properties.actions)) {
        actionOutputLinks[actionName] = actionData.outputsLink;
        actionInputLinks[actionName] = actionData.inputsLink;

        // Check if this is an Agent-type action
        // The type can come from the action data or the workflow definition
        const actionType = actionData.type || workflowDef?.actions?.[actionName]?.type;
        if (actionType?.toLowerCase() === 'agent' || actionType?.toLowerCase() === 'foundryagentservice') {
          agentActionNames.push(actionName);
        }
      }
    }

    // Fetch agent tool links and flatten them into actionOutputLinks/actionInputLinks
    // Keys are the tool action names directly (e.g., "GetOrderDetails")
    const agentToolLinksPromises = agentActionNames.map(async (agentName) => {
      try {
        return await fetchAgentRepetitionToolLinksFlattened(opts, runId, agentName);
      } catch (error) {
        console.warn(`[fetchRunWithAgentToolsWithOutputLinks] Failed to fetch tool links for agent "${agentName}":`, error);
        return { outputLinks: {}, inputLinks: {} };
      }
    });

    const agentToolLinksResults = await Promise.all(agentToolLinksPromises);
    for (const { outputLinks, inputLinks } of agentToolLinksResults) {
      Object.assign(actionOutputLinks, outputLinks);
      Object.assign(actionInputLinks, inputLinks);
    }

    return {
      runId: azureRun.name,
      status: transformRunStatus(azureRun.properties.status),
      startTime: azureRun.properties.startTime,
      endTime: azureRun.properties.endTime,
      triggerOutputLink: azureRun.properties.trigger?.outputsLink,
      triggerInputLink: azureRun.properties.trigger?.inputsLink,
      actionOutputLinks,
      actionInputLinks,
    };
  } catch (error) {
    console.error('Failed to fetch run with agent tools output links:', error);
    return null;
  }
};

/**
 * Fetches all agent repetitions and their tool links, returning them as flat records.
 * Keys are the tool action names directly (e.g., "GetOrderDetails").
 */
const fetchAgentRepetitionToolLinksFlattened = async (
  opts: MonitoringFetchOptions,
  runId: string,
  agentName: string
): Promise<{ outputLinks: Record<string, ContentLink | undefined>; inputLinks: Record<string, ContentLink | undefined> }> => {
  const baseUrl = getAppUrl(opts);
  const repetitionsUrl = `${baseUrl}/workflows/${opts.workflowName}/runs/${runId}/actions/${agentName}/agentRepetitions?api-version=${apiVersions.otto}`;

  const outputLinks: Record<string, ContentLink | undefined> = {};
  const inputLinks: Record<string, ContentLink | undefined> = {};

  try {
    const response = await appFetch<{
      value: Array<{
        name: string;
        properties: {
          status: AzureRunStatus;
          inputsLink?: ContentLink;
          outputsLink?: ContentLink;
          tools?: Record<string, { iterations: number; status?: AzureRunStatus }>;
        };
      }>;
    }>(repetitionsUrl, opts.accessToken);

    // For each repetition, fetch the tool actions to get their output links
    for (const rep of response.value ?? []) {
      const repetitionName = rep.name;
      const toolsFromRepetition = rep.properties.tools ?? {};

      // Fetch tool action links for this repetition
      const actionsUrl = `${baseUrl}/workflows/${opts.workflowName}/runs/${runId}/actions/${agentName}/agentRepetitions/${repetitionName}/actions?api-version=${apiVersions.otto}`;

      try {
        const actionsResponse = await appFetch<{
          value: Array<{
            name: string;
            properties: {
              actionResults?: Array<{
                name: string;
                inputsLink?: ContentLink;
                outputsLink?: ContentLink;
                status: AzureRunStatus;
              }>;
            };
          }>;
        }>(actionsUrl, opts.accessToken);

        // Each item in value represents a tool wrapper with actionResults
        for (const toolWrapper of actionsResponse.value ?? []) {
          const toolName = toolWrapper.name;
          const actionResults = toolWrapper.properties.actionResults ?? [];

          if (actionResults.length > 0) {
            for (const action of actionResults) {
              // Key by the action name directly (e.g., "GetOrderDetails")
              const key = action.name;
              if (action.outputsLink) outputLinks[key] = action.outputsLink;
              if (action.inputsLink) inputLinks[key] = action.inputsLink;
            }
          } else {
            // Fallback: use tool name as action name
            const key = toolName;
            // No links available in fallback, but record the key for completeness
            outputLinks[key] = undefined;
            inputLinks[key] = undefined;
          }
        }
      } catch (actionsError) {
        // If the /actions endpoint fails, create keys from tools in repetition response
        console.warn(`[fetchAgentRepetitionToolLinksFlattened] /actions endpoint failed for repetition="${repetitionName}":`, actionsError);

        for (const toolName of Object.keys(toolsFromRepetition)) {
          const key = toolName;
          outputLinks[key] = undefined;
          inputLinks[key] = undefined;
        }
      }
    }
  } catch (error) {
    console.error(`[fetchAgentRepetitionToolLinksFlattened] Failed for agent="${agentName}":`, error);
  }

  return { outputLinks, inputLinks };
};

/**
 * Fetches action input/output content from content links
 */
export type ContentFetchResult = ContentFetchError | unknown;

/** Only configured runtime and same-origin proxy links may receive a token. */
const getTrustedContentUrl = (uri: string): URL | undefined => {
  try {
    const url = new URL(uri, document.baseURI);
    if (url.username || url.password) return undefined;
    const isLoopback = ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);
    if (url.protocol !== 'https:' && !(url.protocol === 'http:' && isLoopback)) return undefined;
    const endpoint = getLogicEndpoint();
    if (url.origin === window.location.origin
      || (endpoint && url.origin === new URL(endpoint, window.location.href).origin)) return url;
  } catch {
    // Malformed links never qualify for credential escalation.
  }
  return undefined;
};

const fetchContentResponse = async (uri: string, accessToken: string): Promise<Response> => {
  let response = await fetch(uri);
  if (response.status === 401 || response.status === 403) {
    const trustedUrl = getTrustedContentUrl(uri);
    if (!trustedUrl) return response;
    response = await fetch(trustedUrl.href, {
      redirect: 'error',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
  }
  return response;
};

export const fetchActionContent = async (
  contentLink: ContentLink,
  accessToken: string,
  options: { throwOnError?: boolean } = {},
): Promise<ContentFetchResult> => {
  if (!contentLink.uri) {
    return undefined;
  }

  // Content size limit (2MB)
  if (contentLink.contentSize && contentLink.contentSize > 2097152) {
    return { error: 'Content too large to display' };
  }

  try {
    // Try without auth first (pre-signed URLs), fall back to auth if needed
    const response = await fetchContentResponse(contentLink.uri, accessToken);

    if (!response.ok) {
      if (options.throwOnError) throw new AppFetchError(response.status, 'Failed to load run content');
      // Return error object instead of throwing - allows graceful handling
      return { error: `Server error: ${response.status}`, statusCode: response.status };
    }

    return response.json();
  } catch (error) {
    if (options.throwOnError) throw error;
    // Network errors or other fetch failures - return error object for UI display
    const message = error instanceof Error ? error.message : 'Unknown error';
    return { error: `Failed to load: ${message}` };
  }
};

/** Error shape returned by fetchActionContent on failure. */
export interface ContentFetchError {
  error: string;
  statusCode?: number;
}

// Query consumers must reject transport failures, not cache them as workflow JSON.
const fetchMonitoringContent = async (link: ContentLink, token: string, fallback?: ContentLink): Promise<ContentFetchResult> => {
  try {
    return await fetchActionContent(link, token, { throwOnError: true });
  } catch (error) {
    if (error instanceof AppFetchError && error.status === 404 && fallback) {
      return fetchActionContent(fallback, token, { throwOnError: true });
    }
    throw error;
  }
};

type ActionDetails = Pick<NodeExecution, 'input' | 'output' | 'agentMetadata' | 'retryHistory' | 'canResubmit'>;

/**
 * Fetches detailed action data including inputs and outputs.
 * Note: Some built-in action types may return 404 if they don't store action details
 * in the same way as API connection actions.
 */
export const fetchActionDetails = async (
  opts: MonitoringFetchOptions,
  runId: string,
  actionName: string
): Promise<ActionDetails | null> => {
  const baseUrl = getAppUrl(opts);
  const encodedActionName = encodeURIComponent(actionName);
  const url = `${baseUrl}/workflows/${opts.workflowName}/runs/${runId}/actions/${encodedActionName}?api-version=${apiVersions.otto}`;

  try {
    const action = await appFetch<{
      properties: AzureActionRun;
    }>(url, opts.accessToken).catch(error => {
      // Nested sequencer actions use the repetition API instead.
      if (error instanceof AppFetchError && error.status === 404) return null;
      throw error;
    });
    if (!action) return null;

    const result: ActionDetails = {};
    if (typeof action.properties.canResubmit === 'boolean') {
      result.canResubmit = action.properties.canResubmit;
    }

    // Fire input + output content fetches in parallel — they're independent
    // network calls and the previous sequential awaits added an extra
    // round-trip on every action-details lookup.
    const [input, output] = await Promise.all([
      action.properties.inputsLink
        ? fetchMonitoringContent(action.properties.inputsLink, opts.accessToken)
        : Promise.resolve(undefined),
      action.properties.outputsLink
        ? fetchMonitoringContent(action.properties.outputsLink, opts.accessToken)
        : Promise.resolve(undefined),
    ]);

    if (action.properties.inputsLink) {
      result.input = input;
    }

    if (action.properties.outputsLink) {
      result.output = output;
    }

    if (action.properties.agentMetadata) {
      result.agentMetadata = action.properties.agentMetadata;
    }

    // Also surface retry history from the per-action detail response. The run
    // $expand=properties/actions path (transformRun) maps it too; mapping it
    // here as well means it is filled in whenever either source returns it.
    const retryHistory = transformRetryHistory(action.properties.retryHistory);
    if (retryHistory) {
      result.retryHistory = retryHistory;
    }

    return result;
  } catch (error) {
    console.error('Failed to fetch action details:', error);
    throw error;
  }
};

/**
 * Fetches trigger details including inputs and outputs.
 *
 * Primary path uses the trigger-history endpoint
 *   `/workflows/{name}/triggers/{trigger}/histories/{runId}`
 * which works for published workflows on both the local StandardRuntimeHost
 * and the deployed cloud runtime.
 *
 * The runtime checks current published/draft definitions before trigger
 * history lookup. For runs whose trigger was renamed/removed, it can return
 * `WorkflowTriggerNotFound` even though the run itself exists. In that case
 * we fall back to the run record's `properties.trigger.inputsLink/outputsLink`,
 * which use a run-scoped content URL template
 *   `workflows/{flowId}/runs/{runName}/contents/{contentName}`
 * that doesn't validate against the published definition.
 */
export const fetchTriggerDetails = async (
  opts: MonitoringFetchOptions,
  runId: string,
  triggerName: string
): Promise<{ input?: unknown; output?: unknown } | null> => {
  try {
    const { inputsLink, outputsLink } = await fetchTriggerContentLinks(opts, runId, triggerName);
    const [input, output] = await Promise.all([
      inputsLink ? fetchMonitoringContent(inputsLink, opts.accessToken) : Promise.resolve(undefined),
      outputsLink ? fetchMonitoringContent(outputsLink, opts.accessToken) : Promise.resolve(undefined),
    ]);
    const result: { input?: unknown; output?: unknown } = {};
    if (inputsLink) result.input = input;
    if (outputsLink) result.output = output;
    return result;
  } catch (error) {
    console.error('Failed to fetch trigger details:', error);
    throw error;
  }
};

const fetchTriggerContentLinks = async (
  opts: MonitoringFetchOptions,
  runId: string,
  triggerName: string,
): Promise<{ inputsLink?: ContentLink; outputsLink?: ContentLink }> => {
  const baseUrl = getAppUrl(opts);
  const workflowPath = `${baseUrl}/workflows/${encodeURIComponent(opts.workflowName)}`;
  const runUrl = `${workflowPath}/runs/${encodeURIComponent(runId)}?api-version=${apiVersions.otto}`;
  const run = await appFetch<AzureRunResponse>(runUrl, opts.accessToken);

  if (run.properties?.workflow?.mode?.toLowerCase() === 'draft') {
    return run.properties.trigger ?? {};
  }

  try {
    const triggerHistoryUrl = `${workflowPath}/triggers/${encodeURIComponent(triggerName)}/histories/${encodeURIComponent(runId)}?api-version=${apiVersions.otto}`;
    const trigger = await appFetch<{
      properties: {
        inputsLink?: ContentLink;
        outputsLink?: ContentLink;
      };
    }>(triggerHistoryUrl, opts.accessToken);

    return trigger.properties;
  } catch (error) {
    if (error instanceof AppFetchError && error.status === 404 && getRuntimeErrorCode(error.body) === 'WorkflowTriggerNotFound') {
      const runDetails = await appFetch<AzureRunResponse>(runUrl, opts.accessToken);
      return runDetails.properties.trigger ?? {};
    }
    throw error;
  }
};

const getRuntimeErrorCode = (body: string): string | undefined => {
  try {
    const value: unknown = JSON.parse(body);
    if (typeof value !== 'object' || value === null || !('error' in value)) return undefined;
    const error = value.error;
    return typeof error === 'object' && error !== null && 'code' in error && typeof error.code === 'string'
      ? error.code : undefined;
  } catch {
    // An HTTP error may have a non-JSON body. Never display or log that body.
    return undefined;
  }
};

const requestContentFailure = (status?: number): TriggerContentState => {
  telemetryService.trackException(new Error('Request trigger content retrieval failed'), {
    context: 'request_trigger_content',
    ...(status !== undefined ? { statusCode: status } : {}),
  });
  return {
    status: 'error',
    reason: status === 401 || status === 403 ? 'accessDenied' : 'retrieval',
    retryable: status === undefined || status >= 500 || status === 408 || status === 429,
  };
};

export const fetchRequestTriggerContent = async (link: ContentLink | undefined, token: string): Promise<TriggerContentState> => {
  if (!link?.uri) return { status: 'unavailable' };
  if (link.contentSize && link.contentSize > 2097152) {
    return { status: 'error', reason: 'tooLarge', retryable: false };
  }
  try {
    const response = await fetchContentResponse(link.uri, token);
    if (!response.ok) {
      const code = getRuntimeErrorCode(await response.text());
      if (code === 'ContentSecured') return { status: 'secured' };
      if (response.status === 404) return { status: 'unavailable' };
      return requestContentFailure(response.status);
    }
    return { status: 'available', data: await response.json() };
  } catch {
    return requestContentFailure();
  }
};

export interface RequestTriggerDetails {
  requestContent: RequestTriggerContent;
  requestContentLinks?: { input: ContentLink | undefined; output: ContentLink | undefined };
  requestMetadataError?: boolean;
  input?: unknown;
  output?: unknown;
}

/** Explicit transport states keep legitimate JSON (including { error: ... }) intact. */
export const fetchRequestTriggerDetails = async (
  opts: MonitoringFetchOptions,
  runId: string,
  triggerName: string,
): Promise<RequestTriggerDetails> => {
  try {
    const links = await fetchTriggerContentLinks(opts, runId, triggerName);
    const [input, output] = await Promise.all([
      fetchRequestTriggerContent(links.inputsLink, opts.accessToken),
      fetchRequestTriggerContent(links.outputsLink, opts.accessToken),
    ]);
    return {
      requestContent: { input, output },
      requestContentLinks: { input: links.inputsLink, output: links.outputsLink },
      ...(input.status === 'available' ? { input: input.data } : {}),
      ...(output.status === 'available' ? { output: output.data } : {}),
    };
  } catch (error) {
    const state: TriggerContentState = error instanceof AppFetchError && getRuntimeErrorCode(error.body) === 'ContentSecured'
      ? { status: 'secured' }
      : requestContentFailure(error instanceof AppFetchError ? error.status : undefined);
    return { requestContent: { input: state, output: state }, requestMetadataError: true };
  }
};

// Repetition response types for forEach/Until loops
type AzureRepetitionResponse = {
  name: string;
  type: string;
  properties: {
    startTime?: string;
    endTime?: string;
    status: AzureRunStatus;
    repetitionIndexes?: Array<{
      itemIndex?: number;
      scopeName?: string;
    }>;
    inputs?: unknown;
    outputs?: unknown;
    inputsLink?: ContentLink;
    outputsLink?: ContentLink;
    error?: {
      code: string;
      message: string;
    };
  };
};

type AzureRepetitionsResponse = {
  value: AzureRepetitionResponse[];
  nextLink?: string;
};

export type RepetitionInfo = {
  name: string;
  index: number;
  status: NodeExecutionStatus;
  startTime?: string;
  endTime?: string;
  duration?: number;
  error?: {
    code: string;
    message: string;
  };
};

export type RepetitionsPage = {
  repetitions: RepetitionInfo[];
  nextLink?: string;
};

/**
 * Fetches repetitions for a scope action (forEach, Until, etc.)
 * The API endpoint is: /runs/{runId}/actions/{scopeName}/scopeRepetitions
 */
export const fetchActionRepetitions = async (
  opts: MonitoringFetchOptions,
  runId: string,
  scopeName: string
): Promise<RepetitionsPage> => {
  const baseUrl = getAppUrl(opts);
  const encodedScopeName = encodeURIComponent(scopeName);
  const url = `${baseUrl}/workflows/${opts.workflowName}/runs/${runId}/actions/${encodedScopeName}/scopeRepetitions?api-version=${apiVersions.otto}`;

  try {
    const response = await appFetch<AzureRepetitionsResponse>(url, opts.accessToken);

    if (!response.value || !Array.isArray(response.value)) {
      console.warn('[fetchActionRepetitions] Unexpected response structure for', scopeName, response);
      return { repetitions: [] };
    }

    const repetitions: RepetitionInfo[] = response.value.map((rep, index) => {
      const itemIndex = rep.properties.repetitionIndexes?.[0]?.itemIndex ?? index;
      const info: RepetitionInfo = {
        name: rep.name,
        index: itemIndex,
        status: transformNodeStatus(rep.properties.status),
      };
      if (rep.properties.startTime) info.startTime = rep.properties.startTime;
      if (rep.properties.endTime) info.endTime = rep.properties.endTime;
      const duration = calculateDuration(rep.properties.startTime, rep.properties.endTime);
      if (duration !== undefined) info.duration = duration;
      if (rep.properties.error) info.error = rep.properties.error;
      return info;
    });

    const result: RepetitionsPage = { repetitions };
    if (response.nextLink) {
      result.nextLink = response.nextLink;
    }

    return result;
  } catch (error) {
    console.error(`[fetchActionRepetitions] Failed for scope="${scopeName}" url="${url}":`, error);
    throw error;
  }
};

/**
 * Fetches repetitions for a child action inside a scope (ForEach/Until).
 * Uses the /actions/{actionName}/repetitions endpoint which works for both
 * ForEach and Until children. (The /scopeRepetitions endpoint only works for ForEach.)
 */
export const fetchChildActionRepetitions = async (
  opts: MonitoringFetchOptions,
  runId: string,
  actionName: string
): Promise<RepetitionsPage> => {
  const baseUrl = getAppUrl(opts);
  const encodedActionName = encodeURIComponent(actionName);
  const url = `${baseUrl}/workflows/${opts.workflowName}/runs/${runId}/actions/${encodedActionName}/repetitions?api-version=${apiVersions.otto}`;

  try {
    const response = await appFetch<AzureRepetitionsResponse>(url, opts.accessToken);

    if (!response.value || !Array.isArray(response.value)) {
      console.warn('[fetchChildActionRepetitions] Unexpected response structure for', actionName, response);
      return { repetitions: [] };
    }

    const repetitions: RepetitionInfo[] = response.value.map((rep, index) => {
      const itemIndex = rep.properties.repetitionIndexes?.[0]?.itemIndex ?? index;
      const info: RepetitionInfo = {
        name: rep.name,
        index: itemIndex,
        status: transformNodeStatus(rep.properties.status),
      };
      if (rep.properties.startTime) info.startTime = rep.properties.startTime;
      if (rep.properties.endTime) info.endTime = rep.properties.endTime;
      const duration = calculateDuration(rep.properties.startTime, rep.properties.endTime);
      if (duration !== undefined) info.duration = duration;
      if (rep.properties.error) info.error = rep.properties.error;
      return info;
    });

    const result: RepetitionsPage = { repetitions };
    if (response.nextLink) {
      result.nextLink = response.nextLink;
    }

    return result;
  } catch (error) {
    console.error(`[fetchChildActionRepetitions] Failed for action="${actionName}" url="${url}":`, error);
    throw error;
  }
};

/**
 * Fetches more repetitions using continuation token
 */
export const fetchMoreRepetitions = async (
  continuationToken: string,
  accessToken: string
): Promise<RepetitionsPage> => {
  const response = await appFetch<AzureRepetitionsResponse>(continuationToken, accessToken);

  const repetitions: RepetitionInfo[] = response.value.map((rep, index) => {
    const itemIndex = rep.properties.repetitionIndexes?.[0]?.itemIndex ?? index;
    const info: RepetitionInfo = {
      name: rep.name,
      index: itemIndex,
      status: transformNodeStatus(rep.properties.status),
    };
    if (rep.properties.startTime) info.startTime = rep.properties.startTime;
    if (rep.properties.endTime) info.endTime = rep.properties.endTime;
    const duration = calculateDuration(rep.properties.startTime, rep.properties.endTime);
    if (duration !== undefined) info.duration = duration;
    if (rep.properties.error) info.error = rep.properties.error;
    return info;
  });

  const result: RepetitionsPage = { repetitions };
  if (response.nextLink) {
    result.nextLink = response.nextLink;
  }

  return result;
};

/**
 * Fetches input/output details for a specific inner action repetition.
 * Uses /actions/{actionName}/repetitions/{repetitionName} to get the
 * action-level repetition record with inputsLink/outputsLink.
 */
export const fetchRepetitionDetails = async (
  opts: MonitoringFetchOptions,
  runId: string,
  actionName: string,
  repetitionName: string
): Promise<{ input?: unknown; output?: unknown } | null> => {
  const baseUrl = getAppUrl(opts);
  const encodedActionName = encodeURIComponent(actionName);
  const url = `${baseUrl}/workflows/${opts.workflowName}/runs/${runId}/actions/${encodedActionName}/repetitions/${repetitionName}?api-version=${apiVersions.otto}`;

  try {
    const repetition = await appFetch<AzureRepetitionResponse>(url, opts.accessToken);

    const result: { input?: unknown; output?: unknown } = {};

    // Try inline expanded content first, then follow content links
    if (repetition.properties.inputs !== undefined) {
      result.input = repetition.properties.inputs;
    } else if (repetition.properties.inputsLink) {
      result.input = await fetchMonitoringContent(repetition.properties.inputsLink, opts.accessToken);
    }

    if (repetition.properties.outputs !== undefined) {
      result.output = repetition.properties.outputs;
    } else if (repetition.properties.outputsLink) {
      result.output = await fetchMonitoringContent(repetition.properties.outputsLink, opts.accessToken);
    }

    return result;
  } catch (error) {
    console.error(`[fetchRepetitionDetails] Failed for action="${actionName}" repetition="${repetitionName}":`, error);
    throw error;
  }
};

/**
 * Fetches the per-iteration execution data for child actions of a scope (ForEach,
 * Until, etc.) at a specific repetition.
 *
 * Otto's runtime does not expose `/actions/{scope}/repetitions/{rep}/scopeRepetitions`
 * (it 404s), but it DOES expose `/actions/{childName}/repetitions/{rep}` for each
 * child action. This helper fans out per-child requests in parallel (with bounded
 * concurrency) and combines them into a `NodeExecution[]` mirroring the per-iteration
 * shape the canvas/LogsPanel expect.
 *
 * If `childNames` is empty, returns an empty array. 404s for individual children
 * are swallowed (the child may not have run for this iteration if the parent
 * failed early).
 */
export const fetchRepetitionScopeActions = async (
  opts: MonitoringFetchOptions,
  runId: string,
  scopeName: string,
  repetitionName: string,
  childNames: string[] = []
): Promise<NodeExecution[]> => {
  if (childNames.length === 0) {
    return [];
  }

  const baseUrl = getAppUrl(opts);

  // Cap concurrency to avoid hammering the runtime when scopes have many children.
  const CONCURRENCY = 6;
  const results: NodeExecution[] = [];

  const fetchOne = async (childName: string): Promise<NodeExecution | null> => {
    const encodedChildName = encodeURIComponent(childName);
    const url = `${baseUrl}/workflows/${opts.workflowName}/runs/${runId}/actions/${encodedChildName}/repetitions/${repetitionName}?api-version=${apiVersions.otto}`;

    try {
      const repetition = await appFetch<AzureRepetitionResponse>(url, opts.accessToken).catch(error => {
        // A missing child may not have executed if its parent failed early.
        if (error instanceof AppFetchError && error.status === 404) return null;
        throw error;
      });
      if (!repetition) return null;
      const props = repetition.properties;

      const nodeExec: NodeExecution = {
        nodeId: `action-${childName}`,
        nodeName: childName,
        // The /repetitions/{id} response's `type` is the resource type
        // (".../repetitions"), not the underlying action type (e.g. "Http").
        // Consumers (per-iteration override merge) only read status/duration/
        // timing from these results and keep nodeType from the base run, so
        // we use a generic 'Action' default rather than a misleading split.
        nodeType: 'Action',
        status: transformNodeStatus(props.status),
      };
      if (props.startTime) nodeExec.startTime = props.startTime;
      if (props.endTime) nodeExec.endTime = props.endTime;
      const duration = calculateDuration(props.startTime, props.endTime);
      if (duration !== undefined) nodeExec.duration = duration;
      if (props.error) nodeExec.error = props.error;

      if (props.inputs !== undefined) {
        nodeExec.input = props.inputs;
      } else if (props.inputsLink) {
        nodeExec.input = await fetchMonitoringContent(props.inputsLink, opts.accessToken);
      }
      if (props.outputs !== undefined) {
        nodeExec.output = props.outputs;
      } else if (props.outputsLink) {
        nodeExec.output = await fetchMonitoringContent(props.outputsLink, opts.accessToken);
      }

      return nodeExec;
    } catch (error) {
      console.warn(
        `[fetchRepetitionScopeActions] Failed for scope="${scopeName}" child="${childName}" repetition="${repetitionName}":`,
        error
      );
      throw error;
    }
  };

  // Process children in batches of CONCURRENCY.
  for (let i = 0; i < childNames.length; i += CONCURRENCY) {
    const batch = childNames.slice(i, i + CONCURRENCY);
    const settled = await Promise.all(batch.map(fetchOne));
    for (const exec of settled) {
      if (exec) results.push(exec);
    }
  }

  return results;
};

// =============================================================================
// AGENT REPETITIONS API
// =============================================================================

/**
 * Built-in agent tools that don't appear in the /actions endpoint.
 * These tools have their input/output at the iteration level, not as separate actions.
 * Add new built-in tools here as they become available.
 */
export const BUILT_IN_AGENT_TOOLS = ['code_interpreter', 'file_search'] as const;
export type BuiltInAgentTool = typeof BUILT_IN_AGENT_TOOLS[number];

/**
 * Check if a tool name is a built-in agent tool
 */
export const isBuiltInAgentTool = (toolName: string): toolName is BuiltInAgentTool => {
  return BUILT_IN_AGENT_TOOLS.includes(toolName as BuiltInAgentTool);
};

/**
 * Agent repetition response from Azure
 */
type AzureAgentRepetitionResponse = {
  id: string;
  name: string;
  type: string;
  properties: {
    status: AzureRunStatus;
    startTime?: string;
    endTime?: string;
    inputsLink?: ContentLink;
    outputsLink?: ContentLink;
    repetitionIndexes?: Array<{
      scopeName: string;
      itemIndex: number;
    }>;
    /** Tools invoked in this iteration - populated by runtime */
    tools?: Record<string, {
      iterations: number;
      status?: AzureRunStatus;
    }>;
    /** Agent metadata including agent name (from timeline API) */
    agentMetadata?: {
      taskSequenceId?: string;
      agentName?: string;
    };
  };
};

type AzureAgentRepetitionsResponse = {
  value: AzureAgentRepetitionResponse[];
  nextLink?: string;
};

/**
 * Agent iteration info for UI
 */
export type AgentIterationInfo = {
  /** Iteration name/id (e.g., "000000", "000001") */
  name: string;
  /** 0-based index */
  index: number;
  status: NodeExecutionStatus;
  startTime: string | undefined;
  endTime: string | undefined;
  duration: number | undefined;
  /** Tools invoked in this iteration */
  tools: Array<{
    toolName: string;
    iterations: number;
    status: NodeExecutionStatus;
  }>;
  /**
   * Content links for built-in tool input/output.
   * Built-in tools (code_interpreter, file_search) don't appear in the /actions endpoint,
   * so their input/output must be fetched from these iteration-level links.
   */
  inputsLink?: ContentLink;
  outputsLink?: ContentLink;
};

/**
 * Fetches the input/output payloads for a single hosted-agent iteration.
 * Agent iteration content is stored on the repetition's own content links,
 * not on the action-level links used by the parent Agent action.
 */
export const fetchAgentIterationContent = async (
  iterationInfo: AgentIterationInfo,
  accessToken: string
): Promise<{ input?: unknown; output?: unknown }> => {
  const [input, output] = await Promise.all([
    iterationInfo.inputsLink?.uri
      ? fetchMonitoringContent(iterationInfo.inputsLink, accessToken)
      : Promise.resolve(undefined),
    iterationInfo.outputsLink?.uri
      ? fetchMonitoringContent(iterationInfo.outputsLink, accessToken)
      : Promise.resolve(undefined),
  ]);

  const result: { input?: unknown; output?: unknown } = {};
  if (iterationInfo.inputsLink?.uri) result.input = input;
  if (iterationInfo.outputsLink?.uri) result.output = output;
  return result;
};

/**
 * Timeline response from the /timeline endpoint (api-version=2024-04-01)
 * This endpoint returns all agent iterations in a single call
 */
type AzureTimelineResponse = {
  value: AzureAgentRepetitionResponse[];
  nextLink?: string;
};

/**
 * Fetches all agent iterations from the timeline endpoint.
 * This is the preferred method for getting agent iterations as it returns all iterations
 * for all agents in a single call using api-version=2024-04-01.
 *
 * The API endpoint is: /runs/{runId}/timeline?api-version=2024-04-01
 */
export const fetchRunTimeline = async (
  opts: MonitoringFetchOptions,
  runId: string
): Promise<Map<string, AgentIterationInfo[]>> => {
  const baseUrl = getAppUrl(opts);
  const url = `${baseUrl}/workflows/${opts.workflowName}/runs/${runId}/timeline?api-version=${apiVersions.otto}`;

  try {
    const response = await appFetch<AzureTimelineResponse>(url, opts.accessToken);

    // Group iterations by agent name
    const iterationsByAgent = new Map<string, AgentIterationInfo[]>();

    for (const rep of response.value ?? []) {

      // Parse the agent name from the agentMetadata
      // Fallback: try repetitionIndexes[0].scopeName if agentMetadata.agentName is missing
      const agentName = rep.properties.agentMetadata?.agentName
        ?? rep.properties.repetitionIndexes?.[0]?.scopeName;
      if (!agentName) {
        continue;
      }
      // Parse the iteration name to get the index (e.g., "000000" -> 0)
      const iterationIndex = parseInt(rep.name, 10) || 0;
      const tools = rep.properties.tools ?? {};

      const iterationInfo: AgentIterationInfo = {
        name: rep.name,
        index: iterationIndex,
        status: transformNodeStatus(rep.properties.status),
        startTime: rep.properties.startTime,
        endTime: rep.properties.endTime,
        duration: calculateDuration(rep.properties.startTime, rep.properties.endTime),
        tools: Object.entries(tools).map(([toolName, toolData]) => ({
          toolName,
          iterations: toolData.iterations,
          status: transformNodeStatus(toolData.status ?? rep.properties.status),
        })),
      };

      // Capture content links for built-in tools (exactOptionalPropertyTypes)
      if (rep.properties.inputsLink) {
        iterationInfo.inputsLink = rep.properties.inputsLink;
      }
      if (rep.properties.outputsLink) {
        iterationInfo.outputsLink = rep.properties.outputsLink;
      }

      if (!iterationsByAgent.has(agentName)) {
        iterationsByAgent.set(agentName, []);
      }
      iterationsByAgent.get(agentName)!.push(iterationInfo);
    }

    // Sort iterations by index within each agent
    for (const [, iterations] of iterationsByAgent) {
      iterations.sort((a, b) => a.index - b.index);
    }

    return iterationsByAgent;
  } catch (error) {
    console.error('[fetchRunTimeline] Failed to fetch timeline:', error);
    throw error;
  }
};

/**
 * @deprecated Use fetchRunTimeline instead, which returns all agent iterations in a single call
 * Fetches agent repetitions (iterations) for an agent action
 * The API endpoint is: /runs/{runId}/actions/{agentName}/agentRepetitions
 */
export const fetchAgentRepetitions = async (
  opts: MonitoringFetchOptions,
  runId: string,
  agentName: string
): Promise<AgentIterationInfo[]> => {
  const baseUrl = getAppUrl(opts);
  const url = `${baseUrl}/workflows/${opts.workflowName}/runs/${runId}/actions/${agentName}/agentRepetitions?api-version=${apiVersions.otto}`;

  try {
    const response = await appFetch<AzureAgentRepetitionsResponse>(url, opts.accessToken);

    return response.value.map((rep, index) => {
      // Parse the iteration name to get the index (e.g., "000000" -> 0)
      const iterationIndex = parseInt(rep.name, 10) || index;
      const tools = rep.properties.tools ?? {};

      const iterationInfo: AgentIterationInfo = {
        name: rep.name,
        index: iterationIndex,
        status: transformNodeStatus(rep.properties.status),
        startTime: rep.properties.startTime,
        endTime: rep.properties.endTime,
        duration: calculateDuration(rep.properties.startTime, rep.properties.endTime),
        tools: Object.entries(tools).map(([toolName, toolData]) => ({
          toolName,
          iterations: toolData.iterations,
          status: transformNodeStatus(toolData.status ?? rep.properties.status),
        })),
      };

      // Capture content links for built-in tools (exactOptionalPropertyTypes)
      if (rep.properties.inputsLink) {
        iterationInfo.inputsLink = rep.properties.inputsLink;
      }
      if (rep.properties.outputsLink) {
        iterationInfo.outputsLink = rep.properties.outputsLink;
      }

      return iterationInfo;
    });
  } catch (error) {
    console.error('Failed to fetch agent repetitions:', error);
    throw error;
  }
};

/**
 * Tool action info from agent repetition
 */
export type AgentToolActionInfo = {
  /** Tool name (e.g., "Tool_2") - from the parent wrapper */
  toolName: string;
  /** Action name (e.g., "Get_current_weather") - the actual action executed */
  actionName: string;
  status: NodeExecutionStatus;
  startTime: string | undefined;
  endTime: string | undefined;
  duration: number | undefined;
  input?: unknown;
  output?: unknown;
  /** Error details if the action failed */
  error?: {
    code: string;
    message: string;
  };
};

/**
 * Action result within a tool execution
 */
type ToolActionResult = {
  name: string;
  inputsLink?: ContentLink;
  outputsLink?: ContentLink;
  startTime?: string;
  endTime?: string;
  code?: string;
  status: AzureRunStatus;
  error?: {
    code: string;
    message: string;
  };
};

/**
 * Response structure for agent repetition actions
 */
type AgentRepetitionActionsResponse = {
  value: Array<{
    id: string;
    name: string;  // Tool name (e.g., "Get_current_weather")
    type: string;
    properties: {
      actionResults?: ToolActionResult[];
      canResubmit?: boolean;
    };
  }>;
};

/**
 * Built-in tool info passed to fetchAgentRepetitionActions for handling
 * tools like code_interpreter that don't appear in the /actions endpoint.
 */
export type BuiltInToolIterationInfo = {
  /** Tools in this iteration (includes both built-in and workflow tools) */
  tools: Array<{
    toolName: string;
    status: NodeExecutionStatus;
  }>;
  /** Content links for built-in tools - fetched from iteration level */
  inputsLink?: ContentLink;
  outputsLink?: ContentLink;
  /** Iteration timing for built-in tools */
  startTime?: string;
  endTime?: string;
};

/**
 * Fetches tool actions within an agent repetition
 * The API endpoint is: /runs/{runId}/actions/{agentName}/agentRepetitions/{repetitionName}/actions
 *
 * Returns the actual actions (like "Get_current_weather") executed within each tool.
 *
 * For built-in tools (code_interpreter, file_search), pass iterationInfo to include them
 * in the results. Built-in tools don't appear in the /actions endpoint, so their
 * input/output is fetched from the iteration-level content links.
 *
 * @param opts - Monitoring fetch options
 * @param runId - The run ID
 * @param agentName - The agent action name
 * @param repetitionName - The iteration name (e.g., "000000")
 * @param iterationInfo - Optional iteration info for handling built-in tools
 */
export const fetchAgentRepetitionActions = async (
  opts: MonitoringFetchOptions,
  runId: string,
  agentName: string,
  repetitionName: string,
  iterationInfo?: BuiltInToolIterationInfo
): Promise<AgentToolActionInfo[]> => {
  const baseUrl = getAppUrl(opts);
  const url = `${baseUrl}/workflows/${opts.workflowName}/runs/${runId}/actions/${agentName}/agentRepetitions/${repetitionName}/actions?api-version=${apiVersions.otto}`;

  const results: AgentToolActionInfo[] = [];
  const processedToolNames = new Set<string>();

  // Track built-in tools that appear in the /actions response - we'll handle them separately
  // because their action-level content links return 404, we need iteration-level links
  const builtInToolsFromActions: Array<{
    toolName: string;
    actionName: string;
    status: NodeExecutionStatus;
    startTime: string | undefined;
    endTime: string | undefined;
  }> = [];

  try {
    const response = await appFetch<AgentRepetitionActionsResponse>(url, opts.accessToken).catch(error => {
      const onlyBuiltInTools = !!iterationInfo?.tools.length
        && iterationInfo.tools.every(tool => isBuiltInAgentTool(tool.toolName));
      if (error instanceof AppFetchError && error.status === 404 && onlyBuiltInTools) return null;
      throw error;
    });

    // Each item in value represents a tool wrapper (e.g., "Get_current_weather")
    // with actionResults containing the actual action executions
    for (const toolWrapper of response?.value ?? []) {
      const toolName = toolWrapper.name;
      processedToolNames.add(toolName);
      const actionResults = toolWrapper.properties.actionResults ?? [];

      // Check if this is a built-in tool - handle separately with iteration-level content links
      if (isBuiltInAgentTool(toolName)) {
        // Store for later processing with iteration-level content links
        if (actionResults.length > 0) {
          for (const action of actionResults) {
            builtInToolsFromActions.push({
              toolName,
              actionName: action.name,
              status: transformNodeStatus(action.status),
              startTime: action.startTime,
              endTime: action.endTime,
            });
          }
        } else {
          // No actionResults - use iteration-level timing if available
          builtInToolsFromActions.push({
            toolName,
            actionName: toolName,
            status: 'succeeded', // Default status when no action results
            startTime: iterationInfo?.startTime,
            endTime: iterationInfo?.endTime,
          });
        }
        continue; // Skip normal processing - will use iteration-level content links
      }

      // If there are actionResults, use those for details
      if (actionResults.length > 0) {
        for (const action of actionResults) {
          // Fetch input/output content from the links
          let input: ContentFetchResult;
          let output: ContentFetchResult;

          if (action.inputsLink?.uri) {
            // Knowledge hub actions can have broken flat-path content links.
            input = await fetchMonitoringContent(action.inputsLink, opts.accessToken, iterationInfo?.inputsLink);
          }

          if (action.outputsLink?.uri) {
            output = await fetchMonitoringContent(action.outputsLink, opts.accessToken, iterationInfo?.outputsLink);
          }

          results.push({
            toolName,
            actionName: action.name,
            status: transformNodeStatus(action.status),
            startTime: action.startTime,
            endTime: action.endTime,
            duration: calculateDuration(action.startTime, action.endTime),
            input,
            output,
            ...(action.error && { error: action.error }),
          });
        }
      } else {
        // Fallback: just use the tool wrapper info
        results.push({
          toolName,
          actionName: toolName,
          status: 'succeeded', // Default if no action results
          startTime: undefined,
          endTime: undefined,
          duration: undefined,
        });
      }
    }
  } catch (error) {
    console.error('Failed to fetch agent repetition actions:', error);
    throw error;
  }

  // Handle built-in tools using iteration-level content links
  // Built-in tools (code_interpreter, file_search) may appear in /actions but their
  // action-level content links return 404 - we must use iteration-level links
  const allBuiltInTools = [
    ...builtInToolsFromActions, // Tools found in /actions response
    // Also check iterationInfo.tools for any built-in tools not in /actions
    ...(iterationInfo?.tools ?? [])
      .filter(t => isBuiltInAgentTool(t.toolName) && !processedToolNames.has(t.toolName))
      .map(t => ({
        toolName: t.toolName,
        actionName: t.toolName,
        status: t.status,
        startTime: iterationInfo?.startTime,
        endTime: iterationInfo?.endTime,
      })),
  ];

  if (allBuiltInTools.length > 0 && iterationInfo) {
    // Fetch iteration-level input/output for built-in tools (shared across all built-in tools in this iteration)
    let iterationInput: unknown;
    let iterationOutput: unknown;

    if (iterationInfo.inputsLink?.uri) {
      iterationInput = await fetchMonitoringContent(iterationInfo.inputsLink, opts.accessToken);
    }

    if (iterationInfo.outputsLink?.uri) {
      iterationOutput = await fetchMonitoringContent(iterationInfo.outputsLink, opts.accessToken);
    }

    // Create entries for each built-in tool
    for (const tool of allBuiltInTools) {
      results.push({
        toolName: tool.toolName,
        actionName: tool.actionName,
        status: tool.status,
        startTime: tool.startTime,
        endTime: tool.endTime,
        duration: calculateDuration(tool.startTime, tool.endTime),
        input: iterationInput,
        output: iterationOutput,
      });
    }
  }

  return results;
};

// =============================================================================
// CHAT MESSAGE ITERATION API (OMT-based workflows)
// =============================================================================

/**
 * Action info returned from chatMessage iteration API.
 * This mirrors the stateful workflow action structure to support
 * the full breadth of action types (scopes, conditionals, loops, etc.)
 */
export type ChatMessageIterationAction = {
  /** Action name (e.g., "Check_Message_Type", "Normal_Response") */
  name: string;
  /** Action type (e.g., "If", "Compose", "ApiConnection") */
  type: string;
  /** Execution status */
  status: NodeExecutionStatus;
  /** Start time ISO string */
  startTime?: string;
  /** End time ISO string */
  endTime?: string;
  /** Duration in milliseconds */
  duration?: number;
  /** Whether this is a scope action (If, Switch, ForEach, Until, Scope) */
  isScope?: boolean;
  /** For scope actions, the number of child actions or repetitions */
  childCount?: number;
  /** Input content link */
  inputsLink?: ContentLink;
  /** Output content link */
  outputsLink?: ContentLink;
  /** Error details if failed */
  error?: {
    code: string;
    message: string;
  };
};

/**
 * Response structure from the chatMessage iteration actions API.
 * NEW API endpoint: /runs/{runId}/messageIterations/{index}/actions
 * 
 * The new API returns actions in the same schema as stateful workflows,
 * enabling code reuse between stateful and OMT-based chatMessage workflows.
 */
type ChatMessageIterationActionsResponse = {
  value: Array<{
    id: string;
    name: string;
    type: string;
    properties: {
      status: AzureRunStatus;
      startTime?: string;
      endTime?: string;
      inputsLink?: ContentLink;
      outputsLink?: ContentLink;
      code?: string;
      error?: {
        code: string;
        message: string;
      };
      /** Repetition count for agents and scopes */
      repetitionCount?: number;
      /** Whether this action can be resubmitted */
      canResubmit?: boolean;
    };
  }>;
};

/**
 * Fetches actions for a specific chatMessage iteration.
 * 
 * This is designed for OMT-based chatMessage workflows where each message turn
 * can contain the full range of stateful workflow actions (conditions, loops, scopes, etc.).
 * 
 * NEW API endpoint: /runs/{runId}/messageIterations/{index}/actions
 * Returns data in the same schema as stateful workflows, enabling code reuse.
 * 
 * @param opts - Monitoring fetch options including workflowName and accessToken
 * @param runId - The workflow run ID
 * @param iterationIndex - The 0-based iteration index (corresponds to message turn)
 * @returns Array of actions executed in this iteration. Transport failures reject.
 */
export const fetchChatMessageIterationActions = async (
  opts: MonitoringFetchOptions,
  runId: string,
  iterationIndex: number
): Promise<ChatMessageIterationAction[]> => {
  const baseUrl = getAppUrl(opts);
  // New API uses numeric index directly
  const url = `${baseUrl}/workflows/${opts.workflowName}/runs/${runId}/messageIterations/${iterationIndex}/actions?api-version=${apiVersions.otto}`;

  try {
    const response = await appFetch<ChatMessageIterationActionsResponse>(url, opts.accessToken);

    const results: ChatMessageIterationAction[] = [];

    for (const actionDef of response.value ?? []) {
      const actionName = actionDef.name;
      const actionType = actionDef.type?.split('/').pop() ?? 'Action';
      const props = actionDef.properties;

      // Determine if this is a scope-type action
      const scopeTypes = ['if', 'switch', 'foreach', 'until', 'scope'];
      const isScope = scopeTypes.includes(actionType.toLowerCase());

      const action: ChatMessageIterationAction = {
        name: actionName,
        type: actionType,
        status: transformNodeStatus(props.status),
        isScope,
      };

      if (props.startTime) action.startTime = props.startTime;
      if (props.endTime) action.endTime = props.endTime;
      const duration = calculateDuration(props.startTime, props.endTime);
      if (duration !== undefined) action.duration = duration;
      if (props.repetitionCount !== undefined) action.childCount = props.repetitionCount;
      if (props.inputsLink) action.inputsLink = props.inputsLink;
      if (props.outputsLink) action.outputsLink = props.outputsLink;
      if (props.error) action.error = props.error;
      
      results.push(action);
    }

    return results;
  } catch (error) {
    console.error('[fetchChatMessageIterationActions] Failed to fetch:', error);
    throw error;
  }
};

/**
 * Fetches input/output details for a specific action within a chatMessage iteration.
 * 
 * @param opts - Monitoring fetch options
 * @param runId - The workflow run ID
 * @param iterationIndex - The 0-based iteration index
 * @param actionName - The name of the action to fetch details for
 * @returns Input/output data or null on error
 */
export const fetchChatMessageIterationActionDetails = async (
  opts: MonitoringFetchOptions,
  runId: string,
  iterationIndex: number,
  actionName: string
): Promise<{ input?: unknown; output?: unknown } | null> => {
  // First fetch the iteration actions to get the content links
  const actions = await fetchChatMessageIterationActions(opts, runId, iterationIndex);
  const action = actions.find(a => a.name === actionName);

  if (!action) {
    console.error(`[fetchChatMessageIterationActionDetails] Action "${actionName}" not found in iteration ${iterationIndex}`);
    return null;
  }

  const result: { input?: unknown; output?: unknown } = {};

  if (action.inputsLink?.uri) {
    result.input = await fetchMonitoringContent(action.inputsLink, opts.accessToken);
  }

  if (action.outputsLink?.uri) {
    result.output = await fetchMonitoringContent(action.outputsLink, opts.accessToken);
  }

  return result;
};

/**
 * Cancels a running workflow
 */
export const cancelRun = async (
  opts: MonitoringFetchOptions,
  runId: string
): Promise<void> => {
  const baseUrl = getAppUrl(opts);
  const url = `${baseUrl}/workflows/${opts.workflowName}/runs/${runId}/cancel?api-version=${apiVersions.otto}`;

  try {
    await appFetch(url, opts.accessToken, {
      method: 'POST',
    });
  } catch (error) {
    console.error('[cancelRun] Failed to cancel run:', error);
    throw error;
  }

};

/**
 * Replays the selected run's saved trigger data through the current workflow.
 * The trigger name must come from the selected run, not the current definition.
 */
export const resubmitRun = async (
  opts: ResubmissionFetchOptions,
  runId: string,
  triggerName: string
): Promise<void> => {
  await resubmitTriggerHistory(opts, triggerName, runId);
};

/**
 * Starts a new run from one eligible action using the source run's workflow version.
 */
export const resubmitRunFromAction = async (
  opts: ResubmissionFetchOptions,
  runId: string,
  actionName: string
): Promise<void> => {
  const url = `${opts.appUrl ?? getAppUrl(opts)}/workflows/${encodeURIComponent(opts.workflowName)}/runs/${encodeURIComponent(runId)}/resubmit?api-version=${apiVersions.otto}`;
  await appFetch(url, opts.accessToken, {
    method: 'POST',
    body: JSON.stringify({ actionsToResubmit: [{ name: actionName }] }),
  });
};

// Trigger workflow types
export type TriggerType = 'Request' | 'Recurrence' | 'Manual' | 'ApiConnection' | 'ApiConnectionWebhook' | string;

export type TriggerInfo = {
  name: string;
  type: TriggerType;
  /** Trigger kind (e.g., "HTTP" for Request triggers) */
  kind?: string;
  /** Callback URL for HTTP triggers */
  callbackUrl?: string;
  /** HTTP method for the callback (default: POST) */
  method?: string;
  /** Whether the trigger accepts a request body */
  acceptsPayload: boolean;
  /** JSON schema for the expected payload (for Request triggers) */
  payloadSchema?: unknown;
};

/**
 * Payload data for triggering a workflow with custom HTTP options
 */
export type TriggerPayload = {
  /** HTTP method override (default: from trigger or POST) */
  method?: string;
  /** Custom HTTP headers */
  headers?: Record<string, string>;
  /** Query parameters to append to the URL */
  queries?: Record<string, string>;
  /** Request body (will be JSON stringified) */
  body?: unknown;
};

/**
 * Supported trigger types for draft run.
 * Only these trigger types support the /runDraft endpoint.
 */
export const DRAFT_RUN_SUPPORTED_TRIGGERS = [
  'Request',
  'Recurrence',
  'ApiConnection',
  'ApiConnectionNotification',
  'ApiConnectionWebhook',
  'HttpWebhook',
  'Http',
  'SlidingWindow',
  'Manual',
  'Batch',
  'ServiceProvider',
] as const;

/**
 * Check if a trigger type supports draft run
 */
export const canTriggerRunDraft = (triggerType?: string): boolean => {
  if (!triggerType) return false;
  return DRAFT_RUN_SUPPORTED_TRIGGERS.some(
    t => t.toLowerCase() === triggerType.toLowerCase()
  );
};

/**
 * Check if a workflow trigger can be invoked with a custom payload
 * Following LogicAppsUX: only Request triggers with HTTP kind support full payload customization
 */
export const canRunBeInvokedWithPayload = (triggerType?: string, triggerKind?: string): boolean => {
  if (!triggerType) return false;
  const typeMatch = triggerType.toLowerCase() === 'request';
  const kindMatch = !triggerKind || triggerKind.toLowerCase() === 'http';
  return typeMatch && kindMatch;
};

/**
 * Check if a published workflow trigger can be manually run from the portal.
 * This powers rerun availability for completed runs in Monitoring.
 */
export const canTriggerRunPublishedWorkflow = (triggerType?: string): boolean => {
  if (!triggerType) return false;
  const normalizedType = triggerType.toLowerCase();
  return normalizedType === 'request' || normalizedType === 'httpwebhook' || normalizedType === 'manual';
};

/**
 * Whether the trigger type supports the "Test draft" experience.
 * Re-exported from testDataService for convenience.
 */
export { canProvideTestData } from './testDataService';

type CallbackUrlResponse = {
  value: string;
  method?: string;
  basePath?: string;
  queries?: Record<string, string>;
};

/**
 * Fetches the callback URL for a trigger
 * This is used to trigger HTTP Request and Manual triggers
 */
export const fetchTriggerCallbackUrl = async (
  opts: MonitoringFetchOptions,
  triggerName: string
): Promise<CallbackUrlResponse | null> => {
  const baseUrl = getAppUrl(opts);
  const url = `${baseUrl}/workflows/${opts.workflowName}/triggers/${triggerName}/listCallbackUrl?api-version=${apiVersions.otto}`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${opts.accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      // Some triggers (like Recurrence) don't support callback URLs
      if (response.status === 400 || response.status === 404) {
        return null;
      }
      throw new Error(`Failed to fetch callback URL: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Failed to fetch trigger callback URL:', error);
    return null;
  }
};

/**
 * Resolves the result of a *synchronous* workflow trigger call.
 *
 * HTTP Request workflows that end in a Response action return that action's
 * status code synchronously as the HTTP status of the trigger call. A non-2xx
 * status therefore does NOT mean the trigger failed — the workflow ran and
 * deliberately responded with that code (e.g. a 400 "rejected" branch). See
 * issue #2379: surfacing the 400 as "Failed to trigger workflow" is misleading
 * because the run happened and should be shown to the user.
 *
 * The runtime returns the created run's id in the `x-ms-workflow-run-id`
 * response header whenever a run was actually started, so we use the presence
 * of that run id as the source of truth:
 *   - run id present → the workflow ran; report success (with runId) so the
 *     caller navigates to the run, where the response — including any non-2xx
 *     status — is shown in context.
 *   - run id absent → a genuine trigger failure that created no run (auth,
 *     validation, not-found, throttling, infra); report the error.
 *
 * @param response - The fetch Response from the trigger call.
 * @param errorLabel - Prefix for the error message on a genuine failure
 *   (e.g. "Failed to trigger workflow").
 */
const resolveSyncTriggerResult = async (
  response: Response,
  errorLabel: string
): Promise<{ success: boolean; error?: string; runId?: string }> => {
  const runId = response.headers.get('x-ms-workflow-run-id') ?? undefined;

  // A run was created (run id present) — treat as success even on a non-2xx
  // status so the caller routes the user to the run instead of an error.
  if (runId) {
    return { success: true, runId };
  }

  if (response.ok) {
    return { success: true };
  }

  const errorText = await response.text();
  const message = extractErrorMessage(errorText) ?? errorText;
  return {
    success: false,
    error: `${errorLabel} (${response.status}): ${message}`,
  };
};

/**
 * Triggers a workflow run
 * For HTTP Request triggers: POST to the callback URL with optional payload customization
 * For Manual triggers: POST to /triggers/{triggerName}/run
 *
 * @param opts - Monitoring fetch options
 * @param triggerName - Name of the trigger to invoke
 * @param payload - Optional payload with method, headers, queries, and body
 */
export const triggerWorkflow = async (
  opts: MonitoringFetchOptions,
  triggerName: string,
  payload?: TriggerPayload
): Promise<{ success: boolean; error?: string; runId?: string }> => {
  try {
    // First, try to get the callback URL for the trigger
    const callbackInfo = await fetchTriggerCallbackUrl(opts, triggerName);

    let triggerUrl: string;
    let method = 'POST';
    let useCallbackUrl = false;

    if (callbackInfo?.value) {
      // Use the callback URL directly (for HTTP Request triggers)
      triggerUrl = callbackInfo.value;
      // Use payload method override, then callback method, then default to POST
      method = payload?.method ?? callbackInfo.method ?? 'POST';
      useCallbackUrl = true;
    } else {
      // Fall back to the direct trigger run endpoint (for Manual triggers)
      const baseUrl = getAppUrl(opts);
      triggerUrl = `${baseUrl}/workflows/${opts.workflowName}/triggers/${triggerName}/run?api-version=${apiVersions.otto}`;
      // For non-callback URLs, still allow method override from payload
      method = payload?.method ?? 'POST';
    }

    // Append query parameters from payload if provided
    if (payload?.queries && Object.keys(payload.queries).length > 0) {
      const url = new URL(triggerUrl);
      for (const [key, value] of Object.entries(payload.queries)) {
        url.searchParams.append(key, value);
      }
      triggerUrl = url.toString();
    }

    // Build headers: start with default Content-Type, merge custom headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(payload?.headers ?? {}),
    };

    // Note: Callback URLs contain SAS auth in query params (sig=...)
    // Do NOT add Authorization header when using callback URL to avoid
    // "DirectApiRequestHasMoreThanOneAuthorization" error
    // Only add Bearer token for non-callback URLs (and when not in local mode)
    if (!useCallbackUrl && !isLocalMode()) {
      headers['Authorization'] = `Bearer ${opts.accessToken}`;
    }

    const fetchOptions: RequestInit = {
      method,
      headers,
    };

    // Add body if provided
    if (payload?.body !== undefined) {
      fetchOptions.body = typeof payload.body === 'string'
        ? payload.body
        : JSON.stringify(payload.body);
    }

    const response = await fetch(triggerUrl, fetchOptions);
    return await resolveSyncTriggerResult(response, 'Failed to trigger workflow');
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error triggering workflow',
    };
  }
};

/**
 * Triggers a DRAFT workflow run.
 * Uses the /runDraft or /runDraftWithPayload endpoint instead of the callback URL.
 * 
 * Important: The draft must be saved before calling this function.
 * A 500ms delay after save is recommended to avoid 500 errors.
 * 
 * @param opts - Monitoring fetch options
 * @param triggerName - Name of the trigger to invoke
 * @param payload - Optional payload with method, headers, queries, and body
 */
export const triggerDraftWorkflow = async (
  opts: MonitoringFetchOptions,
  triggerName: string,
  payload?: TriggerPayload
): Promise<{ success: boolean; error?: string; runId?: string }> => {
  try {
    const baseUrl = getAppUrl(opts);
    
    // Use runDraftWithPayload if body is provided, otherwise runDraft
    const hasBody = payload?.body !== undefined;
    const endpoint = hasBody ? 'runDraftWithPayload' : 'runDraft';
    
    let triggerUrl = `${baseUrl}/workflows/${opts.workflowName}/triggers/${triggerName}/${endpoint}?api-version=${apiVersions.otto}`;
    
    // Append query parameters from payload if provided
    if (payload?.queries && Object.keys(payload.queries).length > 0) {
      const url = new URL(triggerUrl);
      for (const [key, value] of Object.entries(payload.queries)) {
        url.searchParams.append(key, value);
      }
      triggerUrl = url.toString();
    }

    // Build headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${opts.accessToken}`,
      ...(payload?.headers ?? {}),
    };

    const method = payload?.method ?? 'POST';
    
    const fetchOptions: RequestInit = {
      method,
      headers,
    };

    // Add body if provided
    if (hasBody) {
      fetchOptions.body = typeof payload.body === 'string'
        ? payload.body
        : JSON.stringify(payload.body);
    }

    const response = await fetch(triggerUrl, fetchOptions);
    return await resolveSyncTriggerResult(response, 'Failed to trigger draft workflow');
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error triggering draft workflow',
    };
  }
};

/**
 * Triggers a DRAFT workflow run with mock data for actions that are missing connections.
 * Uses the /submitDraftRun endpoint which supports simulation overrides.
 * 
 * This is used by template mode to run workflows before all connections are configured.
 * Actions without valid connections are mocked with the provided mock definitions.
 * 
 * Important: The draft must be saved before calling this function.
 * A 500ms delay after save is recommended to avoid 500 errors.
 * 
 * @param opts - Monitoring fetch options (projectName, appName, workflowName, accessToken)
 * @param triggerName - Name of the trigger to invoke
 * @param triggerPayload - Optional trigger payload (method, headers, queries, body)
 * @param mockedData - Map of action names to their mock definitions (actions to simulate)
 * @returns Object with success status, optional error message, and optional runId from response headers
 */
export const triggerMockWorkflowRun = async (
  opts: MonitoringFetchOptions,
  triggerName: string,
  triggerPayload: TriggerMockPayload | undefined,
  mockedActions: Record<string, MockNodeInfo>
): Promise<{ success: boolean; error?: string; runId?: string }> => {
  try {
    const baseUrl = getAppUrl(opts);
    const triggerUrl = `${baseUrl}/workflows/${opts.workflowName}/triggers/${triggerName}/submitDraftRun?api-version=${apiVersions.otto}`;
    const payload: {
      trigger?: TriggerMockPayload;
      simulation?: {
        sideEffectPolicy: 'executeUnspecified';
        actionOverrides: Record<string, { behavior: 'mock' | 'live'; result?: MockDefinition }>;
      };
    } = {};

    if (triggerPayload) {
      // Rebuild from the known envelope fields: entries hydrated from older
      // mockData.json sidecars can carry extra fields (e.g. a stale `enabled`
      // toggle) that must not reach the wire — the backend's v1 schema is
      // allowlist-validated and may start rejecting unknowns.
      const { method, headers, queries, body } = triggerPayload;
      payload.trigger = {
        method,
        ...(headers !== undefined && { headers }),
        ...(queries !== undefined && { queries }),
        ...(body !== undefined && { body }),
      };
    }

    // Overrides are only emitted for actions that carry mock data: `mock` when
    // the per-action toggle is on, `live` when the user toggled it off. Actions
    // without mock data are omitted entirely — `executeUnspecified` lets them
    // run live instead of failing the backend's default fail-closed
    // side-effect check. (Entries hydrated from older sidecars may carry a
    // stale `runForReal` flag with no mock data; they fall out here too.)
    const actionOverrides = Object.fromEntries(
      Object.entries(mockedActions)
        .filter(
          (entry): entry is [string, MockNodeInfo & { mockOutput: MockDefinition }] =>
            entry[1].mockOutput !== undefined,
        )
        .map(([actionName, mockNodeInfo]) => [
          // The store uses scoped paths (e.g. "IsUrgent/Post_message")
          // but the backend expects flat action names (leaf segment only).
          actionName.includes('/') ? actionName.slice(actionName.lastIndexOf('/') + 1) : actionName,
          mockNodeInfo.enabled === false
            ? { behavior: 'live' as const }
            : { behavior: 'mock' as const, result: mockNodeInfo.mockOutput },
        ])
    );

    if (Object.keys(actionOverrides).length > 0) {
      payload.simulation = {
        sideEffectPolicy: 'executeUnspecified',
        actionOverrides,
      };
    }

    const headers: Record<string, string> = {
      Authorization: `Bearer ${opts.accessToken}`,
      'Content-Type': 'application/json',
    };

    const response = await fetch(triggerUrl, { method: 'POST', headers, body: JSON.stringify(payload) });
    return await resolveSyncTriggerResult(response, 'Failed to trigger mock workflow run');
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error triggering run for workflow with mocks',
    };
  }
};


/**
 * Build the URL for the streaming draft run endpoint.
 * Returns the full URL including api-version. Auth headers must be added by the caller.
 */
export const buildStreamDraftRunUrl = (
  opts: MonitoringFetchOptions,
  triggerName: string,
): string => {
  const baseUrl = getAppUrl(opts);
  const encodedWorkflowName = encodeURIComponent(opts.workflowName);
  const encodedTriggerName = encodeURIComponent(triggerName);
  return `${baseUrl}/workflows/${encodedWorkflowName}/triggers/${encodedTriggerName}/runDraftWithStream?api-version=${apiVersions.otto}`;
};

/**
 * Fetches the workflow definition to get trigger type information
 * The Azure API returns workflow data in properties.files['workflow.json'].definition
 */
const fetchWorkflowDefinitionTriggers = async (
  opts: MonitoringFetchOptions
): Promise<Record<string, { type: string; kind?: string }>> => {
  const baseUrl = getAppUrl(opts);
  const url = `${baseUrl}/workflows/${opts.workflowName}?api-version=${apiVersions.otto}`;

  try {
    const response = await appFetch<{
      properties?: {
        // Azure API structure: properties.files['workflow.json'].definition.triggers
        files?: {
          'workflow.json'?: {
            definition?: {
              triggers?: Record<string, { type: string; kind?: string }>;
            };
          };
        };
      };
    }>(url, opts.accessToken);

    return response.properties?.files?.['workflow.json']?.definition?.triggers ?? {};
  } catch (error) {
    console.error('Failed to fetch workflow definition:', error);
    return {};
  }
};

/**
 * Fetches information about all triggers in a workflow
 * Used to determine which triggers can be run and what payload they expect
 *
 * Fetches both the triggers API (for names and callback URLs) and the workflow
 * definition (for actual trigger types like Request, Recurrence, etc.)
 */
export const fetchWorkflowTriggers = async (
  opts: MonitoringFetchOptions
): Promise<TriggerInfo[]> => {
  const baseUrl = getAppUrl(opts);
  const url = `${baseUrl}/workflows/${opts.workflowName}/triggers?api-version=${apiVersions.otto}`;

  try {
    // Fetch trigger names from management API and definition in parallel
    const [triggersResponse, definitionTriggers] = await Promise.all([
      appFetch<{
        value: Array<{
          name: string;
          type: string;
          kind?: string;
          properties?: {
            state?: string;
          };
        }>;
      }>(url, opts.accessToken),
      fetchWorkflowDefinitionTriggers(opts),
    ]);

    const triggers: TriggerInfo[] = [];

    for (const trigger of triggersResponse.value) {
      // Get trigger type from the workflow definition (Request, Recurrence, etc.)
      // The management API returns ARM resource type (Microsoft.Logic/workflows/triggers)
      // which is not useful for determining trigger behavior
      const definitionTrigger = definitionTriggers[trigger.name];
      const triggerType = definitionTrigger?.type ?? trigger.type.split('/').pop() ?? trigger.type;
      const triggerKind = definitionTrigger?.kind ?? trigger.kind;

      // Determine if this trigger accepts a payload
      const acceptsPayload = ['Request', 'HttpWebhook', 'Manual'].includes(triggerType);

      const triggerInfo: TriggerInfo = {
        name: trigger.name,
        type: triggerType as TriggerType,
        acceptsPayload,
      };

      // Add kind if available (used for canRunBeInvokedWithPayload check)
      if (triggerKind) {
        triggerInfo.kind = triggerKind;
      }

      // Fetch callback URL for triggers that support it
      if (['Request', 'HttpWebhook', 'Manual'].includes(triggerType)) {
        const callbackInfo = await fetchTriggerCallbackUrl(opts, trigger.name);
        if (callbackInfo?.value) {
          triggerInfo.callbackUrl = callbackInfo.value;
        }
        if (callbackInfo?.method) {
          triggerInfo.method = callbackInfo.method;
        }
      }

      triggers.push(triggerInfo);
    }

    return triggers;
  } catch (error) {
    console.error('Failed to fetch workflow triggers:', error);
    return [];
  }
};

// =============================================================================
// CHAT HISTORY API
// =============================================================================

/**
 * Chat message entry types
 */
export type ChatMessageRole = 'User' | 'Assistant' | 'System' | 'Tool' | 'ToolInvocation';
export type ChatMessageEntryType = 'Content' | 'ToolResult' | 'ToolCall';

/**
 * Tool result payload within a chat message
 */
export type ToolResultPayload = {
  toolResult: {
    status: string;
    toolName: string;
    subIteration?: number;
    /** Tool output content (populated for built-in harness tools like bash, create, grep) */
    content?: string;
  };
};

/**
 * Message content payload
 */
export type MessageContentPayload = {
  content: string;
};

/**
 * Tool call (invocation) payload within a chat message
 */
export type ToolCallPayload = {
  toolCall: {
    toolName: string;
    toolCallId?: string;
    arguments?: Record<string, unknown>;
  };
};

/**
 * Chat message entry from the chatHistory API
 */
export type ChatMessageEntry = {
  /** Type of entry: Content, ToolResult, or ToolCall */
  messageEntryType: ChatMessageEntryType;
  /** Role: User, Assistant, System, Tool, or ToolInvocation */
  role: ChatMessageRole;
  /** Timestamp of the message */
  timestamp: string;
  /** Iteration number (0-indexed) */
  iteration: number;
  /** Content payload for User/Assistant messages */
  messageEntryPayload?: MessageContentPayload;
  /** Tool result payload for Tool messages */
  toolResultsPayload?: ToolResultPayload;
  /** Tool call payload for ToolInvocation messages */
  toolCallPayload?: ToolCallPayload;
  /** Agent metadata */
  agentMetadata?: {
    taskSequenceId?: string;
    agentName?: string;
  };
};

/**
 * Response from the chatHistory API
 */
type AzureChatHistoryResponse = {
  value: ChatMessageEntry[];
};

/**
 * Fetches chat history for a run (A2A/Agent kind workflows).
 * The API endpoint is: /runs/{runId}/chatHistory?api-version=2018-11-01&$expand=properties/actions,workflow/properties
 *
 * Returns chat messages including user messages, assistant responses, and tool results.
 */
export const fetchRunChatHistory = async (
  opts: MonitoringFetchOptions,
  runId: string
): Promise<ChatMessageEntry[]> => {
  const baseUrl = getAppUrl(opts);
  const url = `${baseUrl}/workflows/${opts.workflowName}/runs/${runId}/chatHistory?api-version=${apiVersions.otto}&$expand=properties/actions,workflow/properties`;

  try {
    const response = await appFetch<AzureChatHistoryResponse>(url, opts.accessToken);
    return response.value ?? [];
  } catch (error) {
    console.error('Failed to fetch chat history:', error);
    throw error;
  }
};

/**
 * Fetches chat history for a specific Agent action in a Stateful workflow.
 * The API endpoint is: /runs/{runId}/actions/{actionName}/chatHistory?api-version=2018-11-01
 *
 * This is used for Stateful workflows where the Agent is an action, not the workflow kind.
 * Returns chat messages for that specific Agent action's conversation.
 */
export const fetchActionChatHistory = async (
  opts: MonitoringFetchOptions,
  runId: string,
  actionName: string
): Promise<ChatMessageEntry[]> => {
  const baseUrl = getAppUrl(opts);
  const url = `${baseUrl}/workflows/${opts.workflowName}/runs/${runId}/actions/${actionName}/chatHistory?api-version=${apiVersions.otto}&$expand=properties/actions,workflow/properties`;

  try {
    const response = await appFetch<AzureChatHistoryResponse>(url, opts.accessToken);
    return response.value ?? [];
  } catch (error) {
    console.error(`Failed to fetch action chat history for ${actionName}:`, error);
    throw error;
  }
};

// =============================================================================
// APP-LEVEL RUNS API (for dashboard)
// =============================================================================

export type AppLevelFetchOptions = {
  projectName: string;
  appName: string;
  accessToken: string;
};

/**
 * Dashboard run summary - lighter weight than full Run type
 */
export type DashboardRun = {
  id: string;
  workflowName: string;
  status: RunStatus;
  startTime: string;
  endTime: string | undefined;
  duration: number | undefined;
  isAgentic: boolean;
};

/**
 * Fetches recent runs across all workflows in an app.
 * Uses parallel requests to fetch runs from multiple workflows.
 * @param opts - App-level fetch options including auth
 * @param workflowNames - List of workflow names to fetch runs from
 * @param limit - Maximum number of runs to return (default: 10)
 * @param hoursBack - Only include runs from the last N hours (optional, no filter if not provided)
 */
export const fetchRecentRunsForApp = async (
  opts: AppLevelFetchOptions,
  workflowNames: string[],
  limit: number = 10,
  hoursBack?: number
): Promise<DashboardRun[]> => {
  if (workflowNames.length === 0) {
    return [];
  }

  const baseUrl = getAppUrl(opts);
  const allRuns: DashboardRun[] = [];
  const cutoffTime = hoursBack ? new Date(Date.now() - hoursBack * 60 * 60 * 1000) : null;

  // Fetch runs from each workflow in parallel (limit concurrency to avoid rate limits)
  const batchSize = 5;
  for (let i = 0; i < workflowNames.length; i += batchSize) {
    const batch = workflowNames.slice(i, i + batchSize);
    const batchPromises = batch.map(async (workflowName) => {
      // Fetch more runs per workflow if we're filtering by time, since we may discard some
      const perWorkflowLimit = hoursBack ? 20 : 5;
      const url = `${baseUrl}/workflows/${workflowName}/runs?api-version=${apiVersions.otto}&$top=${perWorkflowLimit}`;
      try {
        const response = await appFetch<AzureRunsResponse>(url, opts.accessToken);
        return response.value.map((run): DashboardRun => ({
          id: run.name,
          workflowName,
          status: transformRunStatus(run.properties.status),
          startTime: run.properties.startTime,
          endTime: run.properties.endTime,
          duration: calculateDuration(run.properties.startTime, run.properties.endTime),
          isAgentic: isAgentWorkflowKind(run.properties.workflow?.kind),
        }));
      } catch {
        // Silently skip workflows that fail (might not have runs yet)
        return [];
      }
    });

    const batchResults = await Promise.all(batchPromises);
    allRuns.push(...batchResults.flat());
  }

  // Filter by time if hoursBack is specified
  const filteredRuns = cutoffTime
    ? allRuns.filter(run => new Date(run.startTime) >= cutoffTime)
    : allRuns;

  // Sort by start time (most recent first) and limit
  return filteredRuns
    .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())
    .slice(0, limit);
};

/**
 * Dashboard metrics summary
 */
export type DashboardMetrics = {
  totalRuns: number;
  failedRuns: number;
  runningRuns: number;
  successRate: number;
};

/**
 * Fetches run metrics across all workflows for a time period (default: last 24 hours)
 */
export const fetchRunMetricsForApp = async (
  opts: AppLevelFetchOptions,
  workflowNames: string[],
  hoursBack: number = 24
): Promise<DashboardMetrics> => {
  if (workflowNames.length === 0) {
    return { totalRuns: 0, failedRuns: 0, runningRuns: 0, successRate: 100 };
  }

  const cutoffTime = new Date(Date.now() - hoursBack * 60 * 60 * 1000).toISOString();
  const baseUrl = getAppUrl(opts);

  let totalRuns = 0;
  let failedRuns = 0;
  let runningRuns = 0;

  // Fetch runs from each workflow in parallel
  const batchSize = 5;
  for (let i = 0; i < workflowNames.length; i += batchSize) {
    const batch = workflowNames.slice(i, i + batchSize);
    const batchPromises = batch.map(async (workflowName) => {
      // Use $filter to get runs after cutoff time
      const url = `${baseUrl}/workflows/${workflowName}/runs?api-version=${apiVersions.otto}&$filter=startTime ge ${cutoffTime}`;
      try {
        const response = await appFetch<AzureRunsResponse>(url, opts.accessToken);
        return response.value;
      } catch {
        return [];
      }
    });

    const batchResults = await Promise.all(batchPromises);
    for (const runs of batchResults) {
      for (const run of runs) {
        totalRuns++;
        const status = transformRunStatus(run.properties.status);
        if (status === 'failed') failedRuns++;
        if (status === 'running') runningRuns++;
      }
    }
  }

  const completedRuns = totalRuns - runningRuns;
  const successRate = completedRuns > 0 ? Math.round(((completedRuns - failedRuns) / completedRuns) * 100) : 100;

  return { totalRuns, failedRuns, runningRuns, successRate };
};

// ---------------------------------------------------------------------------
// Trigger history
// ---------------------------------------------------------------------------
//
// The runtime exposes:
//   GET    .../workflows/{w}/triggers/{t}/histories?api-version=...&$top=...&$filter=...
//   GET    .../workflows/{w}/triggers/{t}/histories/{historyName}
//   POST   .../workflows/{w}/triggers/{t}/histories/{historyName}/resubmit
//
// These already work end-to-end against the deployed Otto control plane via
// the catch-all proxy at /automationProjects/{p}/applications/{a}/{**rest} →
// /runtime/webhooks/workflow/api/management/{**rest}. No control-plane changes
// required.

/** Runtime status casing (TitleCase). */
type AzureTriggerHistoryStatus =
  | 'Running'
  | 'Waiting'
  | 'Succeeded'
  | 'Failed'
  | 'Skipped'
  | 'Cancelled'
  | 'Canceled'
  | 'Aborted';

type AzureTriggerHistoryResponse = {
  id: string;
  name: string;
  type?: string;
  properties: {
    status: AzureTriggerHistoryStatus;
    fired?: boolean;
    startTime: string;
    endTime?: string;
    scheduledTime?: string;
    code?: string;
    error?: { code?: string; message?: string };
    correlation?: { clientTrackingId?: string };
    trackingId?: string;
    inputsLink?: ContentLink;
    outputsLink?: ContentLink;
    run?: { id?: string; name?: string };
  };
};

type AzureTriggerHistoriesResponse = {
  value: AzureTriggerHistoryResponse[];
  nextLink?: string;
};

const transformTriggerHistoryStatus = (
  azureStatus: AzureTriggerHistoryStatus
): TriggerHistoryStatus => {
  switch (azureStatus) {
    case 'Running':
    case 'Waiting':
      return 'running';
    case 'Succeeded':
      return 'succeeded';
    case 'Skipped':
      return 'skipped';
    case 'Failed':
    case 'Aborted':
      return 'failed';
    case 'Cancelled':
    case 'Canceled':
      return 'cancelled';
    default:
      return 'failed';
  }
};

const transformTriggerHistory = (
  azure: AzureTriggerHistoryResponse,
  triggerName: string
): TriggerHistory => {
  const { properties } = azure;
  const errorCode = properties.error?.code;
  const errorMessage = properties.error?.message;
  const error =
    errorCode || errorMessage
      ? { code: errorCode ?? 'Unknown', message: errorMessage ?? '' }
      : undefined;

  const history: TriggerHistory = {
    id: azure.id,
    name: azure.name,
    triggerName,
    status: transformTriggerHistoryStatus(properties.status),
    fired: properties.fired ?? false,
    startTime: properties.startTime,
  };

  if (properties.endTime) history.endTime = properties.endTime;
  if (properties.scheduledTime) history.scheduledTime = properties.scheduledTime;
  if (properties.code) history.code = properties.code;
  if (error) history.error = error;
  if (properties.correlation?.clientTrackingId) {
    history.clientTrackingId = properties.correlation.clientTrackingId;
  }
  if (properties.inputsLink?.uri) {
    history.inputsLink = {
      uri: properties.inputsLink.uri,
      ...(properties.inputsLink.contentSize !== undefined && {
        contentSize: properties.inputsLink.contentSize,
      }),
      ...(properties.inputsLink.contentVersion !== undefined && {
        contentVersion: properties.inputsLink.contentVersion,
      }),
    };
  }
  if (properties.outputsLink?.uri) {
    history.outputsLink = {
      uri: properties.outputsLink.uri,
      ...(properties.outputsLink.contentSize !== undefined && {
        contentSize: properties.outputsLink.contentSize,
      }),
      ...(properties.outputsLink.contentVersion !== undefined && {
        contentVersion: properties.outputsLink.contentVersion,
      }),
    };
  }
  if (properties.run?.name && properties.run?.id) {
    history.run = { name: properties.run.name, id: properties.run.id };
  }

  return history;
};

export type TriggerHistoryListOptions = {
  /** Page size. Defaults to 30; caps server-side payload. */
  top?: number;
  /** Optional client-tracking-id filter (exact match). */
  clientTrackingId?: string;
  /** Optional `startTime ge ...` ISO timestamp filter. */
  startTimeGe?: string;
};

/**
 * Builds the trigger histories URL against the Otto control plane.
 *
 * Pagination uses the same `$skiptoken` trick as `buildRunsListUrl`: the
 * runtime returns a `nextLink` pointing at an ARM URL we cannot reach with
 * an Otto-audience token, so we strip the skiptoken and rebuild against the
 * Otto base.
 */
const buildTriggerHistoriesUrl = (
  opts: MonitoringFetchOptions,
  triggerName: string,
  listOpts: TriggerHistoryListOptions = {},
  continuationToken?: string
): string => {
  const params = new URLSearchParams({ 'api-version': apiVersions.otto });
  if (listOpts.top !== undefined) {
    params.set('$top', String(listOpts.top));
  }

  // Build $filter clauses (the runtime supports `status eq`, `startTime ge`,
  // and `clientTrackingId eq` per FlowRunApiEngine.cs:3255). We currently
  // only send startTime + clientTrackingId on the wire; status filtering is
  // done client-side because the user typically toggles statuses interactively
  // and the response payload is small enough.
  const filters: string[] = [];
  if (listOpts.startTimeGe) {
    filters.push(`startTime ge ${listOpts.startTimeGe}`);
  }
  if (listOpts.clientTrackingId) {
    // Escape single quotes per OData rules.
    const escaped = listOpts.clientTrackingId.replace(/'/g, "''");
    filters.push(`clientTrackingId eq '${escaped}'`);
  }
  if (filters.length > 0) {
    params.set('$filter', filters.join(' and '));
  }

  if (continuationToken) {
    try {
      const skiptoken = new URL(continuationToken).searchParams.get('$skiptoken');
      if (skiptoken) params.set('$skiptoken', skiptoken);
    } catch {
      // malformed continuation — fall back to the first page
    }
  }

  const encodedWorkflowName = encodeURIComponent(opts.workflowName);
  const encodedTriggerName = encodeURIComponent(triggerName);
  return `${getAppUrl(opts)}/workflows/${encodedWorkflowName}/triggers/${encodedTriggerName}/histories?${params.toString()}`;
};

export type TriggerHistoryPage = {
  histories: TriggerHistory[];
  nextLink?: string;
};

/**
 * Fetches a page of trigger histories. Pass the previous page's `nextLink`
 * as `continuationToken` to fetch subsequent pages.
 */
export const fetchTriggerHistoriesPage = async (
  opts: MonitoringFetchOptions,
  triggerName: string,
  listOpts: TriggerHistoryListOptions = {},
  continuationToken?: string
): Promise<TriggerHistoryPage> => {
  const url = buildTriggerHistoriesUrl(opts, triggerName, listOpts, continuationToken);
  const response = await appFetch<AzureTriggerHistoriesResponse>(url, opts.accessToken);

  const result: TriggerHistoryPage = {
    histories: response.value.map((row) => transformTriggerHistory(row, triggerName)),
  };
  if (response.nextLink) {
    result.nextLink = response.nextLink;
  }
  return result;
};

/**
 * Fetches a single trigger history record (metadata only — use
 * `fetchTriggerDetails` for inputs/outputs content).
 */
export const fetchTriggerHistory = async (
  opts: MonitoringFetchOptions,
  triggerName: string,
  historyName: string
): Promise<TriggerHistory | null> => {
  const baseUrl = getAppUrl(opts);
  const encodedWorkflowName = encodeURIComponent(opts.workflowName);
  const encodedTriggerName = encodeURIComponent(triggerName);
  const encodedHistoryName = encodeURIComponent(historyName);
  const url = `${baseUrl}/workflows/${encodedWorkflowName}/triggers/${encodedTriggerName}/histories/${encodedHistoryName}?api-version=${apiVersions.otto}`;

  try {
    const response = await appFetch<AzureTriggerHistoryResponse>(url, opts.accessToken);
    return transformTriggerHistory(response, triggerName);
  } catch (error) {
    console.error('Failed to fetch trigger history:', error);
    return null;
  }
};

/**
 * Resubmits a trigger history row — re-runs the workflow with the original
 * trigger payload. The response body and headers are intentionally not returned;
 * the portal reacts by invalidating the runs/triggerHistories query keys.
 */
export const resubmitTriggerHistory = async (
  opts: ResubmissionFetchOptions,
  triggerName: string,
  historyName: string
): Promise<void> => {
  const baseUrl = opts.appUrl ?? getAppUrl(opts);
  const encodedWorkflowName = encodeURIComponent(opts.workflowName);
  const encodedTriggerName = encodeURIComponent(triggerName);
  const encodedHistoryName = encodeURIComponent(historyName);
  const url = `${baseUrl}/workflows/${encodedWorkflowName}/triggers/${encodedTriggerName}/histories/${encodedHistoryName}/resubmit?api-version=${apiVersions.otto}`;

  await appFetch(url, opts.accessToken, {
    method: 'POST',
    headers: { 'If-Match': '*' },
  });
};
